
/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.lastSearchTerm = '';
      
      // Ready state - tracks if search is fully initialized and ready to use
      this.isReady = false;
      this.initializationPromise = null;

      // Overlay mode detection (must be first)
      this.overlayMode = !!this.querySelector('.predictive-search-overlay');
      this.overlayContainer = this.overlayMode ? this.querySelector('.predictive-search-overlay') : null;
      
      // Always use main input (header search field)
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset') || this.querySelector('.predictive-search-overlay__reset');
      // In overlay mode, use overlay's results container, otherwise use normal results
      if (this.overlayMode) {
        // In overlay mode, ALWAYS use overlay's results container - never fall back to standard container
        if (!this.overlayContainer) {
          if (typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('Overlay mode enabled but overlay container not found');
          }
        }
        this.results = (this.overlayContainer && this.overlayContainer.querySelector('[data-predictive-search-results]')) || (this.overlayContainer && this.overlayContainer.querySelector('#predictive-search-overlay-results'));
        if (!this.results && typeof window !== 'undefined' && window.console && window.console.error) {
          console.error('Overlay results container not found! Make sure [data-predictive-search-results] exists in overlay.');
        }
      } else {
        this.results = this.querySelector('.js-search-results') || this.querySelector('[data-predictive-search-results]');
      }
      this.overlay = this.querySelector('.js-search-overlay') || this.querySelector('.predictive-search-overlay');
      // In overlay mode, statusEl should be in overlay, otherwise in main element
      if (this.overlayMode) {
        this.statusEl = (this.overlayContainer && this.overlayContainer.querySelector('.js-search-status')) || this.querySelector('.js-search-status');
      } else {
      this.statusEl = this.querySelector('.js-search-status');
      }
      this.loadingText = this.getAttribute('data-loading-text');
      
      // Recent searches removed - no longer used
      this.initialContent = null;
      
      // State management
      this.searchInterval = 200; // 200ms debounce like HR mobile
      this.isOverlayOpen = false;
      
      // Only initialize if input exists
      if (!this.input) {
        if (typeof window !== 'undefined' && window.console && window.console.error) {
          console.error('Predictive search: Input field not found (.js-search-input)');
        }
        return;
      }
      
      // DON'T initialize automatically - wait for user interaction (click/focus on search field)
      // This ensures avisbls-container loads on product pages before search overlay initializes
      // Set up basic listeners that will trigger initialization on user interaction
      this.setupLazyInitialization();
    }
    
    /**
     * Set up lazy initialization - only initialize when user clicks/focuses on search input
     * This ensures avisbls-container loads on product pages before search overlay initializes
     */
    setupLazyInitialization() {
      if (!this.input) return;
      
      // Initialize search when user clicks/focuses on search input (lazy initialization)
      const initializeOnInteraction = () => {
        // Fetch the overlay's initial content in parallel with init. This is the
        // earliest signal that the user intends to search, and it runs before the
        // overlay opens, so "Favoritter lige nu" is ready to paint on open.
        this.ensureInitialContent();

        if (this.isReady) return;

        // The handler that opens the overlay on focus is registered inside
        // initialize(), which this very focus event triggers — so the first focus
        // used to initialize and nothing more, and the user had to click again.
        // Open it ourselves once init is done, provided the user is still in the
        // field and hasn't already opened it another way.
        const promise = this.initializationPromise || this.initialize();
        Promise.resolve(promise).then(() => {
          if (this.isOverlayOpen) return;
          if (document.activeElement !== this.input) return;
          this.openOverlay();
        });
      };
      
      // Set up listeners for lazy initialization on main input
      this.input.addEventListener('focus', initializeOnInteraction, { once: true });
      this.input.addEventListener('click', initializeOnInteraction, { once: true });
      
      // Also set up lazy initialization for overlay input (mobile)
      // Note: overlay input might not exist yet in constructor, so we'll set it up in initialize() instead
      // This is handled in initOverlayFeatures() which is called during initialize()
    }
    
    /**
     * Initialize the search functionality
     * This is called asynchronously to avoid blocking the constructor
     */
    async initialize() {
      if (this.initializationPromise) {
        return this.initializationPromise;
      }
      
      this.initializationPromise = (async () => {
        try {
          // Wait for DOM to be ready if not already
          if (document.readyState === 'loading') {
            await new Promise(resolve => {
              if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', resolve, { once: true });
              } else {
                resolve();
              }
            });
          }
          
          // Small delay to ensure all scripts are loaded
          await new Promise(resolve => setTimeout(resolve, 0));
          
          // Initialize listeners and features
      this.addListeners();
          await this.initOverlayFeatures();
          
          // Mark as ready
          this.isReady = true;
          this.setAttribute('data-ready', 'true');
          
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('🔍 Predictive search: Initialized and ready');
          }
        } catch (error) {
          if (typeof window !== 'undefined' && window.console && window.console.error) {
            console.error('🔍 Predictive search: Initialization error:', error);
          }
        }
      })();
      
      return this.initializationPromise;
    }
    
    /**
     * Wait for search to be ready before performing operations
     */
    async waitForReady() {
      if (this.isReady) {
        return;
      }
      
      // Show loading state if input is focused
      if (this.input === document.activeElement) {
        this.input.setAttribute('data-loading', 'true');
        this.input.style.cursor = 'wait';
      }
      
      await this.initialize();
      
      // Remove loading state
      if (this.input) {
        this.input.removeAttribute('data-loading');
        this.input.style.cursor = '';
      }
    }
    
    /**
     * Initialize overlay-specific features
     */
    async initOverlayFeatures() {
      if (!this.overlayMode) return;
      
      // Recent searches removed - no longer used
      
      // The markup itself is fetched on first open, see ensureInitialContent().
      const initialContentContainer = this.querySelector('[data-initial-content]');
      if (initialContentContainer) {
        // Initialize PredictiveSearchInitialContent class
        if (typeof PredictiveSearchInitialContent !== 'undefined') {
          this.initialContent = new PredictiveSearchInitialContent(initialContentContainer);
        } else {
          await this.loadInitialContentScript();
          if (initialContentContainer) {
            this.initialContent = new PredictiveSearchInitialContent(initialContentContainer);
          }
        }
      }
      
          // Setup overlay close buttons with proper cleanup
          const closeButtons = this.overlayContainer.querySelectorAll('.predictive-search-overlay__close, .predictive-search-overlay__footer-close');
          this.overlayCloseButtons = Array.from(closeButtons);
          this.overlayCloseHandler = () => this.close();
          this.overlayCloseButtons.forEach(btn => {
            btn.addEventListener('click', this.overlayCloseHandler);
          });
          
          // Setup footer filter button (mobile only)
          const footerFilterButton = this.overlayContainer.querySelector('[data-footer-filters]');
          if (footerFilterButton) {
            this.footerFilterHandler = () => {
              const sidebarContainer = this.overlayContainer.querySelector('[data-predictive-search-sidebar-container]');
              if (sidebarContainer) {
                // Toggle sidebar visibility
                const isHidden = sidebarContainer.hidden || sidebarContainer.style.display === 'none';
                if (isHidden) {
                  sidebarContainer.hidden = false;
                  sidebarContainer.style.setProperty('display', 'block', 'important');
                } else {
                  sidebarContainer.hidden = true;
                  sidebarContainer.style.setProperty('display', 'none', 'important');
                }
              }
            };
            footerFilterButton.addEventListener('click', this.footerFilterHandler);
          }

          // Mobile: filter icon beside the search bar toggles the slide-down filter panel.
          const mobileFilterToggle = this.overlayContainer.querySelector('[data-mobile-filter-toggle]');
          if (mobileFilterToggle) {
            this.mobileFilterHandler = () => {
              const open = this.overlayContainer.classList.toggle('pso-filters-open');
              mobileFilterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
              mobileFilterToggle.classList.toggle('is-active', open);
            };
            mobileFilterToggle.addEventListener('click', this.mobileFilterHandler);
          }

          // Load-more-on-scroll. The real scroll container is an inner element the theme builds for
          // predictive results (.predictive-search__scroller), NOT .predictive-search-overlay__
          // results — and scroll events don't bubble. So listen in the CAPTURE phase on the overlay
          // and act on whichever descendant inside the results area is the one scrolling.
          if (!this._overlayScrollBound) {
            this._overlayScrollBound = true;
            this.overlayScrollHandler = (e) => {
              if (this._overlayLoadingMore || this._overlayNoMore) return;
              const el = e.target;
              if (!el || typeof el.closest !== 'function') return;
              if (!el.closest('.predictive-search-overlay__results')) return; // ignore facet-panel scroll
              if (typeof el.scrollTop !== 'number' || !el.scrollHeight) return;
              // Trigger ~1.5 product rows before the end so the next page is ready by the time the
              // user reaches it (smoother infinite scroll).
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1400) this.loadMoreOverlayResults();
            };
            this.overlayContainer.addEventListener('scroll', this.overlayScrollHandler, { capture: true, passive: true });
          }

          // Full-width scroll: the results box is centered (max-width) on very wide screens, so a
          // wheel over the side margins wouldn't scroll it. Forward those wheel events to the
          // results scroller. (Over the scroller itself the browser scrolls natively — skip those.)
          if (!this._overlayWheelBound) {
            this._overlayWheelBound = true;
            this.overlayWheelHandler = (e) => {
              const scroller = this.overlayContainer.querySelector('.predictive-search-overlay__results');
              if (!scroller) return;
              if (e.target && e.target.closest && e.target.closest('.predictive-search-overlay__results')) return;
              if (e.target && e.target.closest && e.target.closest('.disclosure__panel')) return;
              scroller.scrollTop += e.deltaY;
            };
            this.overlayContainer.addEventListener('wheel', this.overlayWheelHandler, { passive: true });
          }

          // When main input is focused, open overlay
          if (this.input) {
            this.inputFocusHandler = () => {
              if (!this.isOverlayOpen) {
                this.openOverlay();
              }
            };
            this.input.addEventListener('focus', this.inputFocusHandler);
          }
          
          // Setup overlay search input (mobile only) - sync with main input
          const overlayInput = this.overlayContainer && this.overlayContainer.querySelector('.js-search-input-overlay');
          if (overlayInput) {
            // Sync overlay input with main input value
            const syncOverlayInput = () => {
              if (this.input && overlayInput.value !== this.input.value) {
                overlayInput.value = this.input.value;
              }
            };
            
            // Sync main input with overlay input value and trigger search
            const syncMainInput = () => {
              if (this.input && this.input.value !== overlayInput.value) {
                this.input.value = overlayInput.value;
                // Trigger input event on main input to update search
                const inputEvent = new Event('input', { bubbles: true, cancelable: true });
                this.input.dispatchEvent(inputEvent);
              }
            };
            
            // Handle overlay input events (same as main input)
            const handleOverlayInput = async (e) => {
              await this.waitForReady();
              syncMainInput();
              // Also trigger handleInput directly
              await this.handleInput(e);
            };
            
            // Sync on overlay input change
            overlayInput.addEventListener('input', handleOverlayInput);
            overlayInput.addEventListener('keyup', syncMainInput);
            
            // Focus handler for overlay input
            overlayInput.addEventListener('focus', async () => {
              await this.waitForReady();
              if (!this.isOverlayOpen) {
                this.openOverlay();
              }
            });
            
            // Sync on main input change
            if (this.input) {
              this.input.addEventListener('input', syncOverlayInput);
            }
            
            // Store handlers for cleanup
            this.overlayInputSyncHandler = syncMainInput;
            this.overlayInputHandler = handleOverlayInput;
            this.mainInputSyncHandler = syncOverlayInput;
            
            // Focus overlay input when overlay opens (mobile)
            const focusOverlayInput = () => {
              if (window.innerWidth <= 991 && overlayInput) {
                setTimeout(() => {
                  overlayInput.focus();
                }, 100);
              }
            };
            
            // Focus overlay input when overlay opens
            if (this.overlayContainer) {
              const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                  if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
                    if (!this.overlayContainer.hidden) {
                      focusOverlayInput();
                    }
                  }
                });
              });
              observer.observe(this.overlayContainer, { attributes: true });
              this.overlayObserver = observer;
            }
          }
    }
    
    /**
     * Load recent searches script dynamically
     */
    async loadRecentSearchesScript() {
      return new Promise((resolve, reject) => {
        if (window.PredictiveSearchRecentSearches) {
          resolve();
          return;
        }
        // Script should be loaded via Liquid in the template
        // If not available, try to find it
        const existingScript = document.querySelector('script[src*="predictive-search-recent-searches"]');
        if (existingScript) {
          if (window.PredictiveSearchRecentSearches) {
            resolve();
          } else {
            existingScript.addEventListener('load', resolve);
            existingScript.addEventListener('error', reject);
          }
        } else {
          // Script not loaded - will need to be loaded in template
          if (typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('predictive-search-recent-searches.js not loaded');
          }
          resolve(); // Resolve anyway to not block
        }
      });
    }
    
    /**
     * Fetch the overlay's initial content the first time it is needed.
     *
     * Resolves once the markup is in the DOM. The promise is cached, so opening
     * the overlay repeatedly costs one request. On failure the overlay still
     * works: the container stays empty and search results render as normal.
     */
    ensureInitialContent() {
      if (this.initialContentPromise) return this.initialContentPromise;

      const container = this.querySelector('[data-initial-content]');
      if (!container) return Promise.resolve();

      const url = container.dataset.initialContentUrl;
      if (!url || container.children.length > 0) return Promise.resolve();

      this.initialContentPromise = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((html) => {
          // The Section Rendering API wraps the output in <div id="shopify-section-...">.
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const section = parsed.querySelector('.shopify-section') || parsed.body;
          container.innerHTML = section.innerHTML;
        })
        .catch((error) => {
          this.initialContentPromise = null;
          if (window.console && window.console.warn) {
            window.console.warn('Predictive search: initial content failed to load', error);
          }
        });

      return this.initialContentPromise;
    }

    /**
     * Show the initial content once its markup has arrived.
     *
     * The overlay can open before initialize() has created this.initialContent —
     * the focus that opens the overlay is also what triggers lazy init. So build
     * the instance here if it is missing instead of silently giving up, which
     * left "Favoritter lige nu" blank until the user typed and cleared the query.
     */
    async revealInitialContent() {
      await this.ensureInitialContent();

      if (!this.isOverlayOpen || this.getQuery().length) return;

      const container = this.querySelector('[data-initial-content]');
      if (!container) return;

      if (!this.initialContent) {
        if (typeof PredictiveSearchInitialContent === 'undefined') {
          await this.loadInitialContentScript();
        }
        if (typeof PredictiveSearchInitialContent === 'undefined') return;
        this.initialContent = new PredictiveSearchInitialContent(container);
      }

      // Re-check: the user may have typed or closed while we were waiting.
      if (!this.isOverlayOpen || this.getQuery().length) return;

      this.initialContent.show();
      this.initialContent.container.style.display = 'block';
    }

    /**
     * Load initial content script dynamically
     */
    async loadInitialContentScript() {
      return new Promise((resolve, reject) => {
        if (window.PredictiveSearchInitialContent) {
          resolve();
          return;
        }
        // Script should be loaded via Liquid in the template
        const existingScript = document.querySelector('script[src*="predictive-search-initial-content"]');
        if (existingScript) {
          if (window.PredictiveSearchInitialContent) {
            resolve();
          } else {
            existingScript.addEventListener('load', resolve);
            existingScript.addEventListener('error', reject);
          }
        } else {
          // Script not loaded - will need to be loaded in template
          if (typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('predictive-search-initial-content.js not loaded');
          }
          resolve(); // Resolve anyway to not block
        }
      });
    }

    /**
     * Triggers when the web component is removed from the DOM
     * Clean up event listeners to prevent memory leaks
     */
    disconnectedCallback() {
      this.close();
      
      // Remove overlay close button listeners
      if (this.overlayCloseButtons) {
        this.overlayCloseButtons.forEach(btn => {
          btn.removeEventListener('click', this.overlayCloseHandler);
        });
      }
      
      // Remove footer filter button listener
      const footerFilterButton = this.overlayContainer && this.overlayContainer.querySelector('[data-footer-filters]');
      if (footerFilterButton && this.footerFilterHandler) {
        footerFilterButton.removeEventListener('click', this.footerFilterHandler);
      }
      
      // Remove input focus listener
      if (this.input && this.inputFocusHandler) {
        this.input.removeEventListener('focus', this.inputFocusHandler);
      }
      
      // Remove overlay input sync listeners
      const overlayInput = this.overlayContainer && this.overlayContainer.querySelector('.js-search-input-overlay');
      if (overlayInput) {
        if (this.overlayInputHandler) {
          overlayInput.removeEventListener('input', this.overlayInputHandler);
        }
        if (this.overlayInputSyncHandler) {
          overlayInput.removeEventListener('keyup', this.overlayInputSyncHandler);
        }
      }
      
      if (this.input && this.mainInputSyncHandler) {
        this.input.removeEventListener('input', this.mainInputSyncHandler);
      }
      
      // Remove overlay observer
      if (this.overlayObserver) {
        this.overlayObserver.disconnect();
      }
      
      // Remove focus trap
      if (this.overlayContainer && this.focusTrapHandler) {
        this.overlayContainer.removeEventListener('keydown', this.focusTrapHandler);
      }
      
      // Restore focus to previously focused element
      if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
        this.previouslyFocusedElement.focus();
      }
    }

    addListeners() {
      if (!this.input) return;
      
      // Bind handlers with ready check
      this.input.addEventListener('focus', () => {
        this.handleFocus().catch(err => {
          if (typeof window !== 'undefined' && window.console && window.console.error) {
            console.error('🔍 Predictive search: Error in handleFocus:', err);
          }
        });
      });
      
      // Use custom debounce with 200ms interval for overlay mode
      if (this.overlayMode) {
        let debounceTimer;
        this.input.addEventListener('input', (e) => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            this.handleInput.call(this, e).catch(err => {
              if (typeof window !== 'undefined' && window.console && window.console.error) {
                console.error('🔍 Predictive search: Error in handleInput:', err);
              }
            });
          }, this.searchInterval);
        });
      } else {
        this.input.addEventListener('input', debounce(() => {
          this.handleInput().catch(err => {
            if (typeof window !== 'undefined' && window.console && window.console.error) {
              console.error('🔍 Predictive search: Error in handleInput:', err);
            }
          });
        }));
      }
      
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      
      const searchForm = this.querySelector('.search') || this.querySelector('.predictive-search-overlay__form');
      if (searchForm) {
        searchForm.addEventListener('submit', this.handleSubmit.bind(this));
      }
      
      // Add resize listener for title height adjustment
      if (this.overlayMode) {
        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            if (this.isOverlayOpen) {
              this.adjustTitleHeights();
            }
          }, 250); // Debounce resize events
        });
      }
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      // In overlay mode on mobile, check overlay input first
      if (this.overlayMode && window.innerWidth <= 991) {
        const overlayInput = this.overlayContainer && this.overlayContainer.querySelector('.js-search-input-overlay');
        if (overlayInput && overlayInput.value.trim()) {
          return overlayInput.value.trim();
        }
      }
      return this.input ? this.input.value.trim() : '';
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    async handleInput() {
      // Wait for search to be ready before processing input
      await this.waitForReady();
      
      if (this.input) {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);
      }

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        // Show initial content and recent searches when input is empty
        if (this.overlayMode) {
          // Ensure overlay is open
          if (!this.isOverlayOpen) {
            this.openOverlay();
          }
          
          // Hide results and results-wrapper
          if (this.results) {
            this.results.innerHTML = '';
            this.results.style.display = 'none';
            this.results.hidden = true;
          }
          
          // CRITICAL: Hide results-wrapper when no search term
          const resultsWrapper = this.overlayContainer && this.overlayContainer.querySelector('.predictive-search-overlay__results-wrapper');
          if (resultsWrapper) {
            resultsWrapper.style.setProperty('display', 'none', 'important');
            resultsWrapper.setAttribute('hidden', '');
            resultsWrapper.hidden = true;
          }
          
          const sidebarContainer = this.overlayContainer && this.overlayContainer.querySelector('[data-predictive-search-sidebar-container]');
          if (sidebarContainer) {
            sidebarContainer.hidden = true;
            sidebarContainer.style.display = 'none';
          }
          
          // Show initial content - use setProperty to override !important
          const initialContentContainer = this.overlayContainer && this.overlayContainer.querySelector('[data-initial-content]');
          if (initialContentContainer) {
            initialContentContainer.style.setProperty('display', 'block', 'important');
            initialContentContainer.removeAttribute('hidden');
            initialContentContainer.hidden = false;
          }
          
          // Wait for the markup before measuring, otherwise adjustTitleHeights()
          // runs against an empty container.
          this.revealInitialContent().then(() => {
            if (!this.initialContent || !this.initialContent.container) return;

            this.initialContent.container.style.setProperty('display', 'block', 'important');
            this.initialContent.container.removeAttribute('hidden');
            this.initialContent.container.hidden = false;

            // Adjust title heights for initial content
            requestAnimationFrame(() => {
              this.waitForImages().then(() => {
                this.adjustTitleHeights();
              }).catch(() => {
                setTimeout(() => {
                  this.adjustTitleHeights();
                }, 100);
              });
            });
          });
          
          // Recent searches removed - no longer used
        } else {
          // In non-overlay mode, close dropdown
        this.close();
        }
        return;
      }

      // Hide initial content and recent searches when searching
      if (this.overlayMode) {
        // Ensure overlay is open
        if (!this.isOverlayOpen) {
          this.openOverlay();
        }
        
        // Hide initial content container directly FIRST
        const initialContentContainer = this.overlayContainer && this.overlayContainer.querySelector('[data-initial-content]');
        if (initialContentContainer) {
          initialContentContainer.style.display = 'none';
          initialContentContainer.setAttribute('hidden', '');
          initialContentContainer.hidden = true;
        }
        
        // Hide initial content via instance
        if (this.initialContent && this.initialContent.container) {
          this.initialContent.hide();
          this.initialContent.container.style.display = 'none';
          this.initialContent.container.setAttribute('hidden', '');
          this.initialContent.container.hidden = true;
        }
        
        // Recent searches removed - no longer used
        
        // Ensure results container is ready to show (but don't show it yet - wait for results)
        if (this.results) {
          this.results.removeAttribute('hidden');
          this.results.hidden = false;
          // Clear any existing content
          this.results.innerHTML = '';
        }
      }

      // Call getResults to fetch and display search results
      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    async handleFocus() {
      // Wait for search to be ready before handling focus
      await this.waitForReady();
      
      const searchTerm = this.getQuery();
      
      // In overlay mode, open overlay on focus even if empty
      if (this.overlayMode) {
        if (!this.isOverlayOpen) {
          this.openOverlay();
        }
        
        // Show initial content if no search term
        if (!searchTerm.length) {
          // Hide results
          if (this.results) {
            this.results.innerHTML = '';
            this.results.style.display = 'none';
          }
          // Show initial content once its markup has arrived.
          this.revealInitialContent();
          // Recent searches removed - no longer used
          return;
        } else {
          // Hide initial content when there's a search term
          if (this.initialContent && this.initialContent.container) {
            this.initialContent.hide();
            this.initialContent.container.style.display = 'none';
          }
          if (this.results) {
            this.results.style.display = 'flex'; // Use flex to match CSS
          }
        }
      } else {
        // Original behavior for dropdown mode
      if (!searchTerm.length) return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else if (searchTerm.length) {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Expands a search query to include variations (e.g., "FLX4" -> ["FLX4", "FLX-4", "FLX 4"])
     * This helps find products with different formatting (hyphens, spaces, etc.)
     * @param {string} searchTerm - Original search query.
     * @returns {Array<string>} - Array of query variations.
     */
    expandQuery(searchTerm) {
      const variations = [searchTerm]; // Always include original query
      
      // Only expand if search term contains alphanumeric characters
      if (!/[A-Za-z0-9]/.test(searchTerm)) {
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Query does not contain alphanumeric characters, skipping expansion');
        }
        return variations;
      }
      
      // Pattern: Letter(s) followed by number(s) - e.g., "FLX4", "DDJ10"
      const letterNumberPattern = /([A-Za-z]+)(\d+)/g;
      const matches = [...searchTerm.matchAll(letterNumberPattern)];
      
      if (matches.length > 0) {
        // For each match, create variations with hyphen and space
        matches.forEach(match => {
          const fullMatch = match[0];
          const letters = match[1];
          const numbers = match[2];
          
          // Add hyphen variation: "FLX4" -> "FLX-4"
          const withHyphen = searchTerm.replace(fullMatch, `${letters}-${numbers}`);
          if (withHyphen !== searchTerm && !variations.includes(withHyphen)) {
            variations.push(withHyphen);
            if (typeof window !== 'undefined' && window.console && window.console.log) {
              console.log('🔍 Predictive search: Added hyphen variation:', withHyphen);
            }
          }
          
          // Add space variation: "FLX4" -> "FLX 4"
          const withSpace = searchTerm.replace(fullMatch, `${letters} ${numbers}`);
          if (withSpace !== searchTerm && !variations.includes(withSpace)) {
            variations.push(withSpace);
            if (typeof window !== 'undefined' && window.console && window.console.log) {
              console.log('🔍 Predictive search: Added space variation:', withSpace);
            }
          }
        });
      }
      
      // Reverse: If search term contains hyphens or spaces, try without them
      // "FLX-4" -> ["FLX-4", "FLX4", "FLX 4"]
      if (searchTerm.includes('-')) {
        const withoutHyphen = searchTerm.replace(/-/g, '');
        if (withoutHyphen !== searchTerm && !variations.includes(withoutHyphen)) {
          variations.push(withoutHyphen);
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('🔍 Predictive search: Added without-hyphen variation:', withoutHyphen);
          }
        }
        
        const hyphenToSpace = searchTerm.replace(/-/g, ' ');
        if (hyphenToSpace !== searchTerm && !variations.includes(hyphenToSpace)) {
          variations.push(hyphenToSpace);
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('🔍 Predictive search: Added hyphen-to-space variation:', hyphenToSpace);
          }
        }
      }
      
      if (searchTerm.includes(' ')) {
        const withoutSpace = searchTerm.replace(/\s+/g, '');
        if (withoutSpace !== searchTerm && !variations.includes(withoutSpace)) {
          variations.push(withoutSpace);
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('🔍 Predictive search: Added without-space variation:', withoutSpace);
          }
        }
        
        const spaceToHyphen = searchTerm.replace(/\s+/g, '-');
        if (spaceToHyphen !== searchTerm && !variations.includes(spaceToHyphen)) {
          variations.push(spaceToHyphen);
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('🔍 Predictive search: Added space-to-hyphen variation:', spaceToHyphen);
          }
        }
      }
      
      // Remove duplicates and return
      const uniqueVariations = [...new Set(variations)];
      
      if (typeof window !== 'undefined' && window.console && window.console.log) {
        console.log('🔍 Predictive search: Query expansion complete. Variations:', uniqueVariations);
      }
      
      return uniqueVariations;
    }

    /**
     * Fetches search results for a single query variation.
     * @param {string} searchTerm - Search query.
     * @param {string} baseSearchParams - Base search parameters (without q=).
     * @returns {Promise<string>} - HTML markup of results.
     */
    async fetchSearchResults(searchTerm, baseSearchParams) {
      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += baseSearchParams;
      
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const responseText = await response.text();
        return responseText;
      } catch (error) {
        if (typeof window !== 'undefined' && window.console && window.console.error) {
          console.error('Predictive search: Error fetching results for query:', searchTerm, error);
        }
        return ''; // Return empty string on error
      }
    }

    /**
     * Combines and deduplicates results from multiple queries.
     * @param {Array<string>} allResultsHtml - Array of HTML responses from different queries.
     * @returns {string} - Combined and deduplicated HTML markup.
     */
    combineAndDeduplicateResults(allResultsHtml) {
      if (allResultsHtml.length === 0) return '';
      
      // Use first result as base
      const baseHtml = allResultsHtml[0];
      if (allResultsHtml.length === 1) {
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Only one result, returning as-is');
        }
        return baseHtml;
      }
      
      const tmpl = document.createElement('template');
      tmpl.innerHTML = baseHtml;
      
      const sectionId = this.overlayMode ? 'shopify-section-predictive-search-overlay' : 'shopify-section-predictive-search';
      let baseSection = tmpl.content.querySelector('#' + sectionId);
      
      if (!baseSection) {
        baseSection = tmpl.content.querySelector('#shopify-section-predictive-search');
      }
      
      if (!baseSection) {
        if (typeof window !== 'undefined' && window.console && window.console.warn) {
          console.warn('🔍 Predictive search: Base section not found, returning base HTML');
        }
        return baseHtml;
      }
      
      // Collect all product IDs and handles from base result
      // Try multiple selectors to find products in different HTML structures
      const seenProductIds = new Set();
      const seenProductHandles = new Set();
      
      // Method 1: Find all product links
      const productLinks = baseSection.querySelectorAll('a[href*="/products/"]');
      
      // Method 2: Find product cards/items
      const productItems = baseSection.querySelectorAll('.predictive-search__item, .predictive-result, .product-card, [data-product-id], li[data-product-id]');
      
      // Method 3: Find product forms (which contain product IDs)
      const productForms = baseSection.querySelectorAll('product-form, form[action*="/cart/add"]');
      
      // Extract from links
      productLinks.forEach(link => {
        const productId = link.getAttribute('data-product-id');
        const productHandle = link.href.match(/\/products\/([^?#]+)/)?.[1];
        
        if (productId) {
          seenProductIds.add(productId);
        }
        if (productHandle) {
          seenProductHandles.add(productHandle.toLowerCase());
        }
      });
      
      // Extract from product items/cards
      productItems.forEach(item => {
        const productId = item.getAttribute('data-product-id');
        const productLink = item.querySelector('a[href*="/products/"]') || item.closest('a[href*="/products/"]');
        if (productLink) {
          const productHandle = productLink.href.match(/\/products\/([^?#]+)/)?.[1];
          if (productHandle) {
            seenProductHandles.add(productHandle.toLowerCase());
          }
        }
        if (productId) {
          seenProductIds.add(productId);
        }
      });
      
      // Extract from product forms
      productForms.forEach(form => {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.value) {
          seenProductIds.add(idInput.value);
        }
        const productLink = form.closest('a[href*="/products/"]') || form.querySelector('a[href*="/products/"]');
        if (productLink) {
          const productHandle = productLink.href.match(/\/products\/([^?#]+)/)?.[1];
          if (productHandle) {
            seenProductHandles.add(productHandle.toLowerCase());
          }
        }
      });
      
      // Find the products list in base section to understand structure
      const baseProductsList = baseSection.querySelector('#header-search-products ul, .predictive-search__list, ul');
      const baseProductItems = baseSection.querySelectorAll('li.predictive-search__item');
      
      if (typeof window !== 'undefined' && window.console && window.console.log) {
        console.log('🔍 Predictive search: Base result extraction:');
        console.log('  - Product links found:', productLinks.length);
        console.log('  - Product items found:', productItems.length);
        console.log('  - Product forms found:', productForms.length);
        console.log('  - Products list found:', !!baseProductsList, baseProductsList ? baseProductsList.tagName + '.' + baseProductsList.className : 'none');
        console.log('  - Base product items (li.predictive-search__item):', baseProductItems.length);
        console.log('  - Products by ID:', seenProductIds.size);
        console.log('  - Products by handle:', seenProductHandles.size);
        console.log('  - All product IDs:', Array.from(seenProductIds));
        console.log('  - All product handles:', Array.from(seenProductHandles));
        
        // Log structure of first product item for debugging
        if (baseProductItems.length > 0) {
          const firstItem = baseProductItems[0];
          console.log('  - First product item structure:', {
            tag: firstItem.tagName,
            classes: firstItem.className,
            children: firstItem.children.length,
            hasProductCard: !!firstItem.querySelector('.predictive-search-overlay__product-card'),
            hasProductForm: !!firstItem.querySelector('product-form'),
            hasProductLink: !!firstItem.querySelector('a[href*="/products/"]')
          });
        }
      }
      
      let addedCount = 0;
      
      // Process additional results and add unique products
      for (let i = 1; i < allResultsHtml.length; i++) {
        const additionalHtml = allResultsHtml[i];
        if (!additionalHtml || additionalHtml.trim().length === 0) continue;
        
        const additionalTmpl = document.createElement('template');
        additionalTmpl.innerHTML = additionalHtml;
        
        const additionalSection = additionalTmpl.content.querySelector('#' + sectionId) ||
                                 additionalTmpl.content.querySelector('#shopify-section-predictive-search');
        
        if (!additionalSection) {
          if (typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('🔍 Predictive search: Additional section not found for variation', i);
          }
          continue;
        }
        
        // Find product containers in additional results - try multiple selectors
        // We need to find the actual product container elements, not just links
        const additionalProductContainers = additionalSection.querySelectorAll(
          '.predictive-search__item, ' +
          'li[data-product-id], ' +
          '.predictive-result, ' +
          '.product-card, ' +
          '[data-product-id], ' +
          'product-form, ' +
          'li:has(a[href*="/products/"])'
        );
        
        // Also find all product links to ensure we don't miss any
        const additionalProductLinks = additionalSection.querySelectorAll('a[href*="/products/"]');
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log(`🔍 Predictive search: Variation ${i} has`, additionalProductContainers.length, 'product containers');
          console.log(`🔍 Predictive search: Variation ${i} has`, additionalProductLinks.length, 'product links');
        }
        
        // Create a Set to track which products we've processed (to avoid duplicates within the same variation)
        const processedInVariation = new Set();
        
        // Process product containers first (these are the actual product cards/items)
        additionalProductContainers.forEach(productContainer => {
          // Find product link within or around this container
          let productLink = productContainer.querySelector('a[href*="/products/"]') || 
                           productContainer.closest('a[href*="/products/"]');
          
          // If container itself is a link
          if (!productLink && productContainer.tagName === 'A' && productContainer.href && productContainer.href.includes('/products/')) {
            productLink = productContainer;
          }
          
          if (!productLink || !productLink.href) return;
          
          const productId = productLink.getAttribute('data-product-id') || 
                           productContainer.getAttribute('data-product-id');
          const productHandle = productLink.href.match(/\/products\/([^?#]+)/)?.[1];
          const handleLower = productHandle ? productHandle.toLowerCase() : null;
          
          // Create a unique key for this product
          const productKey = productId || handleLower || productLink.href;
          if (processedInVariation.has(productKey)) return;
          processedInVariation.add(productKey);
          
          // Check if we've already seen this product (by ID or handle)
          const isDuplicate = (productId && seenProductIds.has(productId)) || 
                             (handleLower && seenProductHandles.has(handleLower));
          
          if (!isDuplicate) {
            // Mark as seen
            if (productId) seenProductIds.add(productId);
            if (handleLower) seenProductHandles.add(handleLower);
            
            // Find where to insert this product (in products list)
            // Try multiple selectors to find the products list - prioritize the one that contains products
            let productsList = baseSection.querySelector('#header-search-products ul');
            
            if (!productsList) {
              // Try finding ul that contains .predictive-search__item
              const allUls = baseSection.querySelectorAll('ul');
              for (let ul of allUls) {
                if (ul.querySelector('.predictive-search__item')) {
                  productsList = ul;
                  break;
                }
              }
            }
            
            if (!productsList) {
              productsList = baseSection.querySelector('.predictive-search__list');
            }
            
            if (!productsList) {
              productsList = baseSection.querySelector('ul');
            }
            
            if (productsList) {
              // CRITICAL: Verify this is the SAME list that will be used for innerHTML
              // Sometimes there are multiple lists and we might add to the wrong one
              const isMainList = productsList.classList.contains('predictive-search__list') || 
                                productsList.closest('#header-search-products') ||
                                productsList === baseSection.querySelector('.predictive-search__list, ul');
              
              if (!isMainList) {
                if (typeof window !== 'undefined' && window.console && window.console.warn) {
                  console.warn(`🔍 Predictive search: ⚠️ Products list might not be the main list, verifying...`);
                }
                // Try to find the main list
                const mainList = baseSection.querySelector('.predictive-search__list') || 
                                baseSection.querySelector('#header-search-products ul') ||
                                baseSection.querySelector('ul');
                if (mainList && mainList !== productsList) {
                  if (typeof window !== 'undefined' && window.console && window.console.warn) {
                    console.warn(`🔍 Predictive search: ⚠️ Found different main list, switching...`);
                  }
                  productsList = mainList;
                }
              }
              
              // Find the correct container to clone
              // The structure is: <li class="predictive-search__item"> containing the product card
              let containerToClone = null;
              
              // If productContainer is already a list item with the right class, use it
              if (productContainer.tagName === 'LI' && productContainer.classList.contains('predictive-search__item')) {
                containerToClone = productContainer;
              } else {
                // Find the closest li.predictive-search__item
                containerToClone = productContainer.closest('li.predictive-search__item');
                
                // If not found, try finding any li that contains this container
                if (!containerToClone) {
                  containerToClone = productContainer.closest('li');
                }
                
                // If still not found, and productContainer is a div, try to find parent li
                if (!containerToClone && productContainer.tagName === 'DIV') {
                  // Look for a sibling or parent li
                  let parent = productContainer.parentElement;
                  while (parent && parent !== baseSection) {
                    if (parent.tagName === 'LI') {
                      containerToClone = parent;
                      break;
                    }
                    parent = parent.parentElement;
                  }
                }
                
                // Last resort: use the container itself if it's a valid element
                if (!containerToClone && (productContainer.tagName === 'LI' || productContainer.tagName === 'DIV')) {
                  containerToClone = productContainer;
                }
              }
              
              if (containerToClone) {
                // Verify the container has content before cloning
                const hasContent = containerToClone.querySelector('.predictive-search-overlay__product-card, .product-card, a[href*="/products/"]') ||
                                  containerToClone.textContent.trim().length > 0;
                
                if (!hasContent) {
                  if (typeof window !== 'undefined' && window.console && window.console.warn) {
                    console.warn(`🔍 Predictive search: ⚠️ Container has no content, skipping:`, productHandle || productId);
                  }
                  return; // Skip this product
                }
                
                const cloned = containerToClone.cloneNode(true);
                
                // Verify cloned element has content
                const clonedHasContent = cloned.querySelector('.predictive-search-overlay__product-card, .product-card, a[href*="/products/"]') ||
                                        cloned.textContent.trim().length > 0;
                
                if (!clonedHasContent) {
                  if (typeof window !== 'undefined' && window.console && window.console.warn) {
                    console.warn(`🔍 Predictive search: ⚠️ Cloned container has no content, skipping:`, productHandle || productId);
                  }
                  return; // Skip this product
                }
                
                // CRITICAL: Insert BEFORE the view-all button (if it exists) to ensure proper order
                // The view-all button should always be last
                const viewAllButton = productsList.querySelector('li.js-submit');
                if (viewAllButton) {
                  productsList.insertBefore(cloned, viewAllButton);
                } else {
                  productsList.appendChild(cloned);
                }
                addedCount++;
                
                // CRITICAL: Verify it was actually added AND is in the correct list
                const wasAdded = productsList.contains(cloned);
                if (!wasAdded) {
                  if (typeof window !== 'undefined' && window.console && window.console.error) {
                    console.error(`🔍 Predictive search: ❌ Failed to add product to list:`, productHandle || productId);
                  }
                  addedCount--; // Adjust count
                  return;
                }
                
                // CRITICAL: Verify the product is in the list that will be used for innerHTML
                const finalList = baseSection.querySelector('.predictive-search__list, ul');
                const isInFinalList = finalList && finalList.contains(cloned);
                if (!isInFinalList && finalList !== productsList) {
                  if (typeof window !== 'undefined' && window.console && window.console.error) {
                    console.error(`🔍 Predictive search: ❌ Product added to wrong list!`, {
                      productHandle: productHandle || productId,
                      addedToList: productsList.className,
                      finalList: finalList ? finalList.className : 'none'
                    });
                  }
                  // Try to move it to the correct list
                  if (finalList) {
                    const viewAllInFinal = finalList.querySelector('li.js-submit');
                    if (viewAllInFinal) {
                      finalList.insertBefore(cloned, viewAllInFinal);
                    } else {
                      finalList.appendChild(cloned);
                    }
                    if (typeof window !== 'undefined' && window.console && window.console.log) {
                      console.log(`🔍 Predictive search: ✅ Moved product to correct list:`, productHandle || productId);
                    }
                  }
                }
                
                if (typeof window !== 'undefined' && window.console && window.console.log) {
                  console.log(`🔍 Predictive search: ✅ Added product from variation ${i}:`, productHandle || productId || 'unknown');
                  console.log(`  - Container tag:`, containerToClone.tagName);
                  console.log(`  - Container classes:`, containerToClone.className);
                  console.log(`  - Products list tag:`, productsList.tagName);
                  console.log(`  - Products list classes:`, productsList.className);
                  console.log(`  - Products in list now:`, productsList.children.length);
                  console.log(`  - Product items (excluding .js-submit):`, productsList.querySelectorAll('li.predictive-search__item:not(.js-submit)').length);
                  console.log(`  - Is in final list:`, isInFinalList || finalList === productsList);
                }
              } else {
                if (typeof window !== 'undefined' && window.console && window.console.warn) {
                  console.warn(`🔍 Predictive search: ⚠️ Could not find container to clone for product:`, productHandle || productId);
                  console.warn(`  - ProductContainer tag:`, productContainer.tagName);
                  console.warn(`  - ProductContainer classes:`, productContainer.className);
                  console.warn(`  - ProductContainer parent:`, productContainer.parentElement ? productContainer.parentElement.tagName : 'none');
                }
              }
            } else {
              if (typeof window !== 'undefined' && window.console && window.console.warn) {
                console.warn('🔍 Predictive search: Products list not found in base section.');
                console.warn('  - Available elements:', {
                  allUls: baseSection.querySelectorAll('ul').length,
                  predictiveList: baseSection.querySelector('.predictive-search__list'),
                  headerProducts: baseSection.querySelector('#header-search-products'),
                  predictiveResults: baseSection.querySelector('.predictive-search__results')
                });
              }
            }
          } else {
            if (typeof window !== 'undefined' && window.console && window.console.log) {
              console.log(`🔍 Predictive search: ⏭️ Skipped duplicate product from variation ${i}:`, productHandle || productId || 'unknown');
            }
          }
        });
        
        // Also process standalone product links that might not be in containers
        additionalProductLinks.forEach(productLink => {
          // Skip if this link is already inside a processed container
          if (productLink.closest('.predictive-search__item, li[data-product-id], .predictive-result')) {
            return;
          }
          
          const productId = productLink.getAttribute('data-product-id');
          const productHandle = productLink.href.match(/\/products\/([^?#]+)/)?.[1];
          const handleLower = productHandle ? productHandle.toLowerCase() : null;
          
          const productKey = productId || handleLower || productLink.href;
          if (processedInVariation.has(productKey)) return;
          processedInVariation.add(productKey);
          
          const isDuplicate = (productId && seenProductIds.has(productId)) || 
                             (handleLower && seenProductHandles.has(handleLower));
          
          if (!isDuplicate) {
            if (productId) seenProductIds.add(productId);
            if (handleLower) seenProductHandles.add(handleLower);
            
            const productsList = baseSection.querySelector(
              '.predictive-search__list, #header-search-products ul, .predictive-search__results, ul'
            );
            
            if (productsList) {
              // Create a wrapper for the standalone link
              const wrapper = document.createElement('li');
              wrapper.className = 'predictive-search__item';
              wrapper.appendChild(productLink.cloneNode(true));
              productsList.appendChild(wrapper);
              addedCount++;
              
              if (typeof window !== 'undefined' && window.console && window.console.log) {
                console.log(`🔍 Predictive search: ✅ Added standalone product link from variation ${i}:`, productHandle || productId);
              }
            }
          }
        });
      }
      
      // Final verification: Count actual items in the list after combining
      const finalProductItems = baseSection.querySelectorAll('li.predictive-search__item:not(.js-submit)');
      const finalProductCount = finalProductItems.length;
      
      // Get the products list to verify structure
      const finalProductsList = baseSection.querySelector('.predictive-search__list, ul');
      
      if (typeof window !== 'undefined' && window.console && window.console.log) {
        console.log('🔍 Predictive search: Added', addedCount, 'new products from variations');
        console.log('🔍 Predictive search: Total unique products (by ID/handle):', seenProductIds.size + (seenProductHandles.size - seenProductIds.size));
        console.log('🔍 Predictive search: Final product items in list:', finalProductCount);
        console.log('🔍 Predictive search: Expected products:', seenProductHandles.size);
        
        if (finalProductsList) {
          console.log('🔍 Predictive search: Final list children count:', finalProductsList.children.length);
          console.log('🔍 Predictive search: Final list innerHTML length:', finalProductsList.innerHTML.length);
          
          // Log all items in final list
          console.log('🔍 Predictive search: Final list items:');
          Array.from(finalProductsList.children).forEach((item, index) => {
            const isViewAll = item.classList.contains('js-submit');
            const productLink = item.querySelector('a[href*="/products/"]');
            const productHandle = productLink ? productLink.href.match(/\/products\/([^?#]+)/)?.[1] : null;
            console.log(`  - Item ${index + 1}:`, {
              tag: item.tagName,
              isViewAll: isViewAll,
              productHandle: productHandle || 'N/A',
              hasProductCard: !!item.querySelector('.predictive-search-overlay__product-card'),
              innerHTMLLength: item.innerHTML.length
            });
          });
        }
        
        if (finalProductCount < seenProductHandles.size) {
          console.warn('🔍 Predictive search: ⚠️ Mismatch! Expected', seenProductHandles.size, 'products but only', finalProductCount, 'items in list');
          console.warn('🔍 Predictive search: Missing products:', {
            expected: Array.from(seenProductHandles),
            actual: Array.from(finalProductItems).map(item => {
              const link = item.querySelector('a[href*="/products/"]');
              return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : 'unknown';
            })
          });
        }
      }
      
      // CRITICAL: Verify the HTML before returning
      // Sometimes innerHTML can lose elements if there are issues with the structure
      const verificationList = baseSection.querySelector('.predictive-search__list, ul');
      if (verificationList) {
        const verificationItems = verificationList.querySelectorAll('li.predictive-search__item:not(.js-submit)');
        if (verificationItems.length !== finalProductCount) {
          if (typeof window !== 'undefined' && window.console && window.console.error) {
            console.error('🔍 Predictive search: ❌ Verification failed! Item count changed:', finalProductCount, '->', verificationItems.length);
          }
        }
        
        // CRITICAL: Verify that innerHTML actually contains all items
        // Sometimes when we modify DOM and then get innerHTML, elements can be lost
        const innerHTMLProductItems = verificationList.innerHTML.match(/<li[^>]*class="[^"]*predictive-search__item[^"]*"[^>]*>/g);
        const innerHTMLProductCount = innerHTMLProductItems ? innerHTMLProductItems.filter(html => !html.includes('js-submit')).length : 0;
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Products in innerHTML (regex count):', innerHTMLProductCount);
          console.log('🔍 Predictive search: Verification list children count:', verificationList.children.length);
          console.log('🔍 Predictive search: Verification list innerHTML length:', verificationList.innerHTML.length);
        }
        
        // CRITICAL: If innerHTML has fewer products, manually rebuild the HTML
        // This ensures all products are included in the returned markup
        if (innerHTMLProductCount < finalProductCount) {
          if (typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('🔍 Predictive search: ⚠️ innerHTML has fewer products than DOM! Rebuilding HTML...', {
              domCount: finalProductCount,
              innerHTMLCount: innerHTMLProductCount
            });
          }
          
          // Manually rebuild the list HTML to ensure all products are included
          // Use outerHTML to preserve all attributes and structure
          let rebuiltListHTML = '';
          const productItems = Array.from(verificationList.children).filter(item => !item.classList.contains('js-submit'));
          const viewAllButton = Array.from(verificationList.children).find(item => item.classList.contains('js-submit'));
          
          // Add all product items first
          productItems.forEach((item) => {
            rebuiltListHTML += item.outerHTML;
          });
          
          // Add view-all button at the end if it exists
          if (viewAllButton) {
            rebuiltListHTML += viewAllButton.outerHTML;
          }
          
          // Replace the list's innerHTML with the rebuilt version
          verificationList.innerHTML = rebuiltListHTML;
          
          // Verify the rebuild worked
          const afterRebuildItems = verificationList.querySelectorAll('li.predictive-search__item:not(.js-submit)');
          const afterRebuildCount = afterRebuildItems.length;
          
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('🔍 Predictive search: After rebuild, product count:', afterRebuildCount);
            console.log('🔍 Predictive search: After rebuild, product handles:', Array.from(afterRebuildItems).map(item => {
              const link = item.querySelector('a[href*="/products/"]');
              return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
            }).filter(Boolean));
          }
          
          // If rebuild still doesn't have all products, log detailed info
          if (afterRebuildCount < finalProductCount) {
            if (typeof window !== 'undefined' && window.console && window.console.error) {
              console.error('🔍 Predictive search: ❌ Rebuild failed! Still missing products:', {
                expected: finalProductCount,
                actual: afterRebuildCount,
                missing: finalProductCount - afterRebuildCount
              });
              
              // Log which products are missing
              const expectedHandles = Array.from(seenProductHandles);
              const actualHandles = Array.from(afterRebuildItems).map(item => {
                const link = item.querySelector('a[href*="/products/"]');
                return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
              }).filter(Boolean);
              
              const missingHandles = expectedHandles.filter(h => !actualHandles.includes(h.toLowerCase()));
              console.error('🔍 Predictive search: Missing product handles:', missingHandles);
            }
          }
        }
      }
      
      // Final check: Ensure baseSection.innerHTML contains all products
      const finalCheckList = baseSection.querySelector('.predictive-search__list, ul');
      if (finalCheckList) {
        const finalCheckItems = finalCheckList.querySelectorAll('li.predictive-search__item:not(.js-submit)');
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Final check before returning innerHTML:', {
            expectedProducts: seenProductHandles.size,
            actualProductsInDOM: finalCheckItems.length,
            allHandles: Array.from(seenProductHandles),
            actualHandles: Array.from(finalCheckItems).map(item => {
              const link = item.querySelector('a[href*="/products/"]');
              return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
            }).filter(Boolean)
          });
        }
        
        // If still missing products, try one more time with direct outerHTML
        if (finalCheckItems.length < seenProductHandles.size) {
          if (typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('🔍 Predictive search: ⚠️ Still missing products before return. Attempting final fix...');
          }
          
          // Get all children as outerHTML
          const allChildrenHTML = Array.from(finalCheckList.children).map(child => child.outerHTML).join('');
          finalCheckList.innerHTML = allChildrenHTML;
          
          const afterFinalFix = finalCheckList.querySelectorAll('li.predictive-search__item:not(.js-submit)').length;
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('🔍 Predictive search: After final fix, product count:', afterFinalFix);
          }
        }
      }
      
      // CRITICAL: Before returning, verify that innerHTML actually contains all products
      // Sometimes innerHTML can lose elements if they're in a different part of the DOM tree
      const finalVerificationList = baseSection.querySelector('.predictive-search__list, ul');
      if (finalVerificationList) {
        const finalVerificationItems = finalVerificationList.querySelectorAll('li.predictive-search__item:not(.js-submit)');
        const finalVerificationCount = finalVerificationItems.length;
        
        // CRITICAL: Also check the actual innerHTML string to see if it contains all products
        const innerHTMLString = baseSection.innerHTML;
        const expectedHandles = Array.from(seenProductHandles);
        const handlesInInnerHTML = expectedHandles.filter(handle => {
          // Check if handle appears in innerHTML (case-insensitive)
          // Look for the handle in product URLs
          return innerHTMLString.toLowerCase().includes(handle.toLowerCase());
        });
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Final verification before return:', {
            expectedProducts: seenProductHandles.size,
            productsInDOM: finalVerificationCount,
            productsInInnerHTML: handlesInInnerHTML.length,
            allExpectedHandles: expectedHandles,
            handlesInInnerHTML: handlesInInnerHTML,
            innerHTMLLength: innerHTMLString.length
          });
          
          // Log which handles are missing from innerHTML
          const missingFromInnerHTML = expectedHandles.filter(h => !handlesInInnerHTML.includes(h));
          if (missingFromInnerHTML.length > 0) {
            console.warn('🔍 Predictive search: ⚠️ Handles missing from innerHTML:', missingFromInnerHTML);
            
            // Try to find where these handles are in the DOM
            missingFromInnerHTML.forEach(handle => {
              const productLink = finalVerificationList.querySelector(`a[href*="${handle}"]`);
              if (productLink) {
                const productItem = productLink.closest('li.predictive-search__item');
                if (productItem) {
                  console.warn(`🔍 Predictive search: Missing handle "${handle}" found in DOM:`, {
                    itemExists: !!productItem,
                    itemInList: finalVerificationList.contains(productItem),
                    itemOuterHTML: productItem.outerHTML.substring(0, 200)
                  });
                }
              }
            });
          }
        }
        
        // If innerHTML is missing products, trigger rebuild
        if (handlesInInnerHTML.length < seenProductHandles.size || finalVerificationCount < seenProductHandles.size) {
          if (typeof window !== 'undefined' && window.console && window.console.error) {
            console.error('🔍 Predictive search: ❌ CRITICAL: innerHTML will lose products!', {
              expected: seenProductHandles.size,
              actual: finalVerificationCount,
              missing: seenProductHandles.size - finalVerificationCount
            });
            
            // Log which products are missing
            const expectedHandles = Array.from(seenProductHandles);
            const actualHandles = Array.from(finalVerificationItems).map(item => {
              const link = item.querySelector('a[href*="/products/"]');
              return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
            }).filter(Boolean);
            
            const missingHandles = expectedHandles.filter(h => !actualHandles.includes(h.toLowerCase()));
            console.error('🔍 Predictive search: Missing product handles:', missingHandles);
            
            // Try to find missing products in other parts of baseSection
            const allProductLinks = baseSection.querySelectorAll('a[href*="/products/"]');
            console.error('🔍 Predictive search: All product links in baseSection:', allProductLinks.length);
            
            const allProductHandles = Array.from(allProductLinks).map(link => {
              return link.href.match(/\/products\/([^?#]+)/)?.[1];
            }).filter(Boolean);
            console.error('🔍 Predictive search: All product handles in baseSection:', allProductHandles);
            
            // Check if missing products are in a different list
            const allLists = baseSection.querySelectorAll('ul');
            console.error('🔍 Predictive search: All lists in baseSection:', allLists.length);
            allLists.forEach((list, index) => {
              const items = list.querySelectorAll('li.predictive-search__item:not(.js-submit)');
              const handles = Array.from(items).map(item => {
                const link = item.querySelector('a[href*="/products/"]');
                return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
              }).filter(Boolean);
              console.error(`🔍 Predictive search: List ${index} (${list.className}):`, {
                items: items.length,
                handles: handles
              });
            });
          }
          
          // CRITICAL: Manually rebuild the entire section HTML to ensure all products are included
          // This is a last resort to ensure we don't lose any products
          if (typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('🔍 Predictive search: ⚠️ Rebuilding entire section HTML to preserve all products...');
          }
          
          // Get all lists and their content
          const allListsInSection = baseSection.querySelectorAll('ul');
          let rebuiltSectionHTML = '';
          
          // Rebuild each list
          allListsInSection.forEach(list => {
            const listItems = Array.from(list.children);
            let listHTML = '<ul';
            if (list.className) {
              listHTML += ' class="' + list.className + '"';
            }
            if (list.id) {
              listHTML += ' id="' + list.id + '"';
            }
            listHTML += '>';
            
            listItems.forEach(item => {
              listHTML += item.outerHTML;
            });
            
            listHTML += '</ul>';
            rebuiltSectionHTML += listHTML;
          });
          
          // Replace the section's content with rebuilt HTML
          baseSection.innerHTML = rebuiltSectionHTML;
          
          // Verify after rebuild
          const afterRebuildVerification = baseSection.querySelectorAll('li.predictive-search__item:not(.js-submit)');
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('🔍 Predictive search: After section rebuild, product count:', afterRebuildVerification.length);
          }
        }
      }
      
      return baseSection.innerHTML;
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';
      if (theme.settings.pSearchIncludeBarcode) searchFields += ',variants.barcode';

      // Build base search parameters (without q=)
      // CRITICAL: Use 'each' scope to get more products (up to limit per type)
      // and specify 'product' type to ensure we only get products
      // NOTE: Shopify's Predictive Search API has a hard limit of 10 results per type
      // We work around this by using query expansion (multiple variations) and combining results
      const limitScope = theme.settings.pSearchLimitScope || 'each';
      // Use maximum allowed by Shopify API (10) - this is a hard limit, cannot be increased
      const searchLimit = Math.min(theme.settings.pSearchLimit || 10, 10);
      
      // Request products only - collections can be added later if needed
      // NOTE: Adding collections to the API call was causing products to not be returned
      // We'll extract collections from the results if they exist, but won't request them explicitly
      let baseSearchParams = `&${encodeURIComponent('resources[type]')}=product`;
      baseSearchParams += `&${encodeURIComponent('resources[limit]')}=${searchLimit}`;
      baseSearchParams += `&${encodeURIComponent('resources[limit_scope]')}=${limitScope}`;
      baseSearchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      // Use overlay section when in overlay mode to get product-card rendering
      baseSearchParams += this.overlayMode ? '&section_id=predictive-search-overlay' : '&section_id=predictive-search';
      
      if (typeof window !== 'undefined' && window.console && window.console.log) {
        console.log('🔍 Predictive search: API params:', baseSearchParams);
      }

      // Generate unique cache key based on search term and filters
      // Use searchTerm directly to ensure different queries get different cache keys
      const queryKey = searchTerm.toLowerCase().trim().replace(/\s+/g, '-');
      
      // Clear cache if search term changed (to force refresh)
      if (this.lastSearchTerm && this.lastSearchTerm !== searchTerm) {
        // Clear cache for old search term
        delete this.cachedResults[this.lastSearchTerm.toLowerCase().trim().replace(/\s+/g, '-')];
      }
      this.lastSearchTerm = searchTerm;
      
      // Only use cache if we have results for this exact search term
      if (this.cachedResults[queryKey] && this.cachedResults[queryKey].trim().length > 0) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        // Query expansion: Generate variations of the search term
        // This helps find products with different formatting (e.g., "FLX4" -> "FLX-4")
        const queryVariations = this.expandQuery(searchTerm);
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Original query:', searchTerm);
          console.log('🔍 Predictive search: Query variations:', queryVariations);
          console.log('🔍 Predictive search: Number of variations:', queryVariations.length);
        }
        
        // Fetch results for all query variations in parallel
        const allResultsPromises = queryVariations.map((variation, index) => {
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log(`🔍 Predictive search: Fetching variation ${index + 1}/${queryVariations.length}: "${variation}"`);
          }
          return this.fetchSearchResults(variation, baseSearchParams);
        });
        
        const allResultsHtml = await Promise.all(allResultsPromises);
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: All results received:', allResultsHtml.length);
          allResultsHtml.forEach((html, index) => {
            console.log(`🔍 Predictive search: Variation ${index + 1} (${queryVariations[index]}):`, 
              html ? `${html.length} chars` : 'empty');
            // Check if products are in the HTML
            if (html) {
              const productCount = (html.match(/\/products\//g) || []).length;
              const hasProducts = html.includes('predictive-search__item') || html.includes('product-card');
              console.log(`🔍 Predictive search: Variation ${index + 1} - Products in HTML:`, productCount, 'links, has products:', hasProducts);
            }
          });
        }
        
        // Filter out empty results
        const validResults = allResultsHtml.filter(html => html && html.trim().length > 0);
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Valid results:', validResults.length, 'out of', allResultsHtml.length);
        }
        
        if (validResults.length === 0) {
          // No results from any variation
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.warn('🔍 Predictive search: No results from any query variation');
          }
          if (this.overlayMode) {
            if (this.initialContent && this.initialContent.container) {
              this.initialContent.hide();
              this.initialContent.container.style.display = 'none';
              this.initialContent.container.setAttribute('hidden', '');
            }
            if (this.results) {
              this.results.innerHTML = '<div class="predictive-search__no-results"><p>Ingen resultater fundet for "' + searchTerm + '"</p></div>';
              this.results.style.display = 'flex';
            }
          }
          return;
        }
        
        // Combine and deduplicate results from all variations
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Combining results from', validResults.length, 'variations');
        }
        const combinedResultsHtml = this.combineAndDeduplicateResults(validResults);
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Combined HTML length:', combinedResultsHtml.length);
        }
        
        // Parse the combined results
        const tmpl = document.createElement('template');
        tmpl.innerHTML = combinedResultsHtml;

        // Find the correct section based on overlay mode
        const sectionId = this.overlayMode ? 'shopify-section-predictive-search-overlay' : 'shopify-section-predictive-search';
        let resultsEl = tmpl.content.querySelector('#' + sectionId);
        
        // CRITICAL: Extract all product handles from combinedResultsHtml BEFORE parsing to detect if any are lost
        const allProductLinksInCombined = combinedResultsHtml.match(/\/products\/([^"?#\s<>]+)/gi) || [];
        const uniqueHandlesInCombined = [...new Set(allProductLinksInCombined.map(link => {
          const match = link.match(/\/products\/([^"?#\s<>]+)/i);
          return match ? match[1].toLowerCase() : null;
        }).filter(Boolean))];
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Unique product handles in combinedResultsHtml:', uniqueHandlesInCombined.length);
          console.log('🔍 Predictive search: Handles found in combinedResultsHtml:', uniqueHandlesInCombined);
        }
        
        
        // Fallback to standard section if overlay section not found
        if (!resultsEl) {
          resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        }
        
        if (!resultsEl) {
          // If combined HTML doesn't have section structure, try using first valid result
          const firstValidTmpl = document.createElement('template');
          firstValidTmpl.innerHTML = validResults[0];
          resultsEl = firstValidTmpl.content.querySelector('#' + sectionId) ||
                     firstValidTmpl.content.querySelector('#shopify-section-predictive-search');
        }
        
        if (!resultsEl) {
          if (typeof window !== 'undefined' && window.console && window.console.error) {
            console.error('Predictive search: Invalid response structure. Looking for #' + sectionId);
            console.error('Combined HTML length:', combinedResultsHtml.length);
          }
          throw new Error('Invalid response structure from predictive search API');
        }
        
        // CRITICAL: Now that resultsEl is guaranteed to exist, check if products were lost during parsing
        if (uniqueHandlesInCombined.length > 0) {
          const productsAfterParsing = resultsEl.querySelectorAll('li.predictive-search__item:not(.js-submit)');
          const handlesAfterParsing = Array.from(productsAfterParsing).map(item => {
            const link = item.querySelector('a[href*="/products/"]');
            return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
          }).filter(Boolean).map(h => h.toLowerCase());
          
          if (handlesAfterParsing.length < uniqueHandlesInCombined.length) {
            if (typeof window !== 'undefined' && window.console && window.console.warn) {
              console.warn('🔍 Predictive search: ⚠️ Products lost during parsing!', {
                beforeParsing: uniqueHandlesInCombined.length,
                afterParsing: handlesAfterParsing.length,
                missing: uniqueHandlesInCombined.filter(h => !handlesAfterParsing.includes(h.toLowerCase()))
              });
            }
            
            // Try to find the missing product in the template content
            const missingHandles = uniqueHandlesInCombined.filter(h => !handlesAfterParsing.includes(h.toLowerCase()));
            missingHandles.forEach(missingHandle => {
              // Search for the product in the entire template
              const allLinks = tmpl.content.querySelectorAll('a[href*="/products/"]');
              const missingProductLink = Array.from(allLinks).find(link => {
                const handle = link.href.match(/\/products\/([^?#]+)/)?.[1];
                return handle && handle.toLowerCase() === missingHandle.toLowerCase();
              });
              
              if (missingProductLink) {
                if (typeof window !== 'undefined' && window.console && window.console.log) {
                  console.log('🔍 Predictive search: Found missing product link in template:', missingHandle);
                }
                
                // Find the product container (li.predictive-search__item)
                let productContainer = missingProductLink.closest('li.predictive-search__item');
                if (!productContainer) {
                  // If not in a list item, try to find parent container
                  productContainer = missingProductLink.closest('li');
                }
                
                if (productContainer) {
                  // Find the products list in resultsEl
                  const productsList = resultsEl.querySelector('.predictive-search__list, ul');
                  if (productsList) {
                    // Clone and add the missing product
                    const cloned = productContainer.cloneNode(true);
                    const viewAllButton = productsList.querySelector('li.js-submit');
                    if (viewAllButton) {
                      productsList.insertBefore(cloned, viewAllButton);
                    } else {
                      productsList.appendChild(cloned);
                    }
                    
                    if (typeof window !== 'undefined' && window.console && window.console.log) {
                      console.log('🔍 Predictive search: ✅ Added missing product back:', missingHandle);
                    }
                  } else {
                    if (typeof window !== 'undefined' && window.console && window.console.warn) {
                      console.warn('🔍 Predictive search: ⚠️ Products list not found in resultsEl');
                    }
                  }
                } else {
                  if (typeof window !== 'undefined' && window.console && window.console.warn) {
                    console.warn('🔍 Predictive search: ⚠️ Product container not found for:', missingHandle);
                  }
                }
              } else {
                if (typeof window !== 'undefined' && window.console && window.console.warn) {
                  console.warn('🔍 Predictive search: ⚠️ Missing product link not found in template:', missingHandle);
                }
              }
            });
          }
        }
        
        // Real Shopify facets + more results are fetched AFTER renderResults sets innerHTML,
        // via the post-render hook calling fetchFullSearchSection(). No synthetic sidebar.

        // CRITICAL: Verify product count in resultsEl BEFORE extracting innerHTML
        const productsInResultsEl = resultsEl.querySelectorAll('li.predictive-search__item:not(.js-submit)');
        const productHandlesInResultsEl = Array.from(productsInResultsEl).map(item => {
          const link = item.querySelector('a[href*="/products/"]');
          return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
        }).filter(Boolean);
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Products in resultsEl BEFORE innerHTML extraction:', productsInResultsEl.length);
          console.log('🔍 Predictive search: Product handles in resultsEl:', productHandlesInResultsEl);
        }
        
        // Safe: Replacing only known pattern with input ID
        let resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);
        
        // CRITICAL: Verify product count in resultsMarkup string
        const allProductLinksInMarkup = resultsMarkup.match(/\/products\/([^"?#\s<>]+)/gi) || [];
        const uniqueHandlesInMarkup = [...new Set(allProductLinksInMarkup.map(link => {
          const match = link.match(/\/products\/([^"?#\s<>]+)/i);
          return match ? match[1].toLowerCase() : null;
        }).filter(Boolean))];
        
        if (typeof window !== 'undefined' && window.console && window.console.log) {
          console.log('🔍 Predictive search: Got results markup, length:', resultsMarkup.length);
          console.log('🔍 Predictive search: Products in resultsMarkup string (by handle):', uniqueHandlesInMarkup.length);
          console.log('🔍 Predictive search: Handles found in markup string:', uniqueHandlesInMarkup);
          // Compare with products in resultsEl, not uniqueHandlesInCombined (which is in a different scope)
          const missingHandles = productHandlesInResultsEl.filter(h => !uniqueHandlesInMarkup.includes(h.toLowerCase()));
          if (missingHandles.length > 0) {
            console.warn('🔍 Predictive search: Missing handles in markup string:', missingHandles);
          }
        }
        
        // If products are missing from markup string, try to fix it
        if (uniqueHandlesInMarkup.length < productsInResultsEl.length) {
            if (typeof window !== 'undefined' && window.console && window.console.warn) {
              console.warn('🔍 Predictive search: ⚠️ Products missing from markup string! Attempting to rebuild...', {
                productsInResultsEl: productsInResultsEl.length,
                uniqueHandlesInMarkup: uniqueHandlesInMarkup.length
              });
            }
            
            // Manually rebuild the innerHTML from the DOM to ensure all products are included
            const productsList = resultsEl.querySelector('.predictive-search__list, ul');
            if (productsList) {
              const allListItems = Array.from(productsList.children);
              let rebuiltListHTML = '';
              allListItems.forEach(item => {
                rebuiltListHTML += item.outerHTML;
              });
              productsList.innerHTML = rebuiltListHTML;
              
              // Get the updated innerHTML
              const updatedResultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);
              
              // Verify the rebuild worked
              const allProductLinksInUpdated = updatedResultsMarkup.match(/\/products\/([^"?#\s<>]+)/gi) || [];
              const uniqueHandlesInUpdated = [...new Set(allProductLinksInUpdated.map(link => {
                const match = link.match(/\/products\/([^"?#\s<>]+)/i);
                return match ? match[1].toLowerCase() : null;
              }).filter(Boolean))];
              
              if (uniqueHandlesInUpdated.length > uniqueHandlesInMarkup.length) {
              if (typeof window !== 'undefined' && window.console && window.console.log) {
                console.log('🔍 Predictive search: ✅ Rebuild successful! Products in updated markup:', uniqueHandlesInUpdated.length);
              }
              // Use the updated markup
              resultsMarkup = updatedResultsMarkup;
            } else {
              if (typeof window !== 'undefined' && window.console && window.console.warn) {
                console.warn('🔍 Predictive search: ⚠️ Rebuild did not help. Still missing products.');
              }
            }
          }
        }

        // Check if we actually got results
        if (!resultsMarkup || resultsMarkup.trim().length === 0) {
          // No results - show empty state but still hide initial content
          if (this.overlayMode) {
            if (this.initialContent && this.initialContent.container) {
              this.initialContent.hide();
              this.initialContent.container.style.display = 'none';
              this.initialContent.container.setAttribute('hidden', '');
            }
            if (this.results) {
              this.results.innerHTML = '<div class="predictive-search__no-results"><p>Ingen resultater fundet for "' + searchTerm + '"</p></div>';
              this.results.style.display = 'flex';
            }
          }
          return;
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        // On error, still hide initial content in overlay mode
        if (this.overlayMode) {
          if (this.initialContent && this.initialContent.container) {
            this.initialContent.hide();
            this.initialContent.container.style.display = 'none';
            this.initialContent.container.setAttribute('hidden', '');
          }
          if (this.results) {
            this.results.innerHTML = '<div class="predictive-search__no-results"><p>Fejl ved søgning. Prøv igen.</p></div>';
            this.results.style.display = 'flex';
          }
        }
        if (typeof window !== 'undefined' && window.console && window.console.error) {
          console.error('Predictive search error:', error);
        }
        // Don't close overlay on error, just show error message
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Waits for all images in results to load
     * @returns {Promise} Resolves when all images are loaded or timeout
     */
    waitForImages() {
      if (!this.results) return Promise.resolve();
      
      const images = this.results.querySelectorAll('img');
      if (images.length === 0) return Promise.resolve();
      
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve, reject) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true }); // Resolve on error too
          // Timeout after 2 seconds
          setTimeout(resolve, 2000);
        });
      });
      
      return Promise.all(imagePromises);
    }

    /**
     * Adjusts title heights dynamically based on row content.
     * If all titles in a row are 2 lines or less, use 2 lines.
     * Otherwise, use 3 lines max.
     */
    adjustTitleHeights() {
      if (!this.overlayMode) return;
      
      // Check both results and initial content
      const resultsContainer = this.results;
      const initialContentContainer = this.overlayContainer && this.overlayContainer.querySelector('[data-initial-content]');
      
      // Process results container
      if (resultsContainer) {
        this.adjustTitleHeightsForContainer(resultsContainer);
      }
      
      // Process initial content container
      if (initialContentContainer && !initialContentContainer.hidden) {
        this.adjustTitleHeightsForContainer(initialContentContainer);
      }
    }
    
    /**
     * Adjusts title heights for a specific container
     * @param {HTMLElement} container - Container to process
     */
    adjustTitleHeightsForContainer(container) {
      // Find all product cards
      const productCards = container.querySelectorAll('.predictive-search-overlay__product-card, .card--product, li.predictive-search__item:not(.js-submit), .card');
      if (productCards.length === 0) return;
      
      // Group cards into rows based on their vertical position
      const rows = [];
      const rowMap = new Map();
      
      productCards.forEach(card => {
        const rect = card.getBoundingClientRect();
        const top = Math.round(rect.top);
        
        if (!rowMap.has(top)) {
          rowMap.set(top, []);
        }
        rowMap.get(top).push(card);
      });
      
      // Convert map to array and sort by top position
      const sortedRows = Array.from(rowMap.entries())
        .sort((a, b) => a[0] - b[0])
        .map(entry => entry[1]);
      
      // Process each row
      sortedRows.forEach(rowCards => {
        const titles = [];
        let maxNaturalHeight = 0;
        
        // Find all titles in this row and measure their natural height
        rowCards.forEach(card => {
          const title = card.querySelector('.card__title, .predictive-result__title, .predictive-search-overlay__initial-content .card__title');
          if (title) {
            // Temporarily remove max-height to measure natural height
            const originalMaxHeight = title.style.maxHeight;
            const originalHeight = title.style.height;
            title.style.maxHeight = 'none';
            title.style.height = 'auto';
            
            // Force a reflow
            void title.offsetHeight;
            
            const naturalHeight = title.scrollHeight;
            maxNaturalHeight = Math.max(maxNaturalHeight, naturalHeight);
            
            titles.push({
              element: title,
              naturalHeight: naturalHeight,
              originalMaxHeight: originalMaxHeight,
              originalHeight: originalHeight
            });
            
            // Restore original styles
            title.style.maxHeight = originalMaxHeight;
            title.style.height = originalHeight;
          }
        });
        
        if (titles.length === 0) return;
        
        // Calculate target height
        // Max 3 lines = 52.5px (14px font * 1.25 line-height * 3 lines = 52.5px)
        // Min 2 lines = 35px (14px font * 1.25 line-height * 2 lines = 35px)
        const maxAllowedHeight = 52.5; // 3 lines
        const minAllowedHeight = 35; // 2 lines
        
        let targetHeight;
        if (maxNaturalHeight <= 35) {
          // All titles are 2 lines or less - use 2 lines
          targetHeight = minAllowedHeight;
        } else if (maxNaturalHeight <= 52.5) {
          // Use natural height if it's between 2-3 lines
          targetHeight = maxNaturalHeight;
        } else {
          // Exceeds 3 lines - cap at 3 lines
          targetHeight = maxAllowedHeight;
        }
        
        // Apply target height to all titles in the row
        titles.forEach(({ element }) => {
          element.style.height = `${targetHeight}px`;
          element.style.maxHeight = `${targetHeight}px`;
        });
      });
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     * Note: innerHTML is safe here as resultsMarkup comes from Shopify's own API response
     */
    renderResults(resultsMarkup) {
      if (!this.results) {
        if (typeof window !== 'undefined' && window.console && window.console.error) {
          console.error('Predictive search: Cannot render results - results container not found');
        }
        return;
      }
      
      // Check if we have existing products to fade out
      const existingItems = this.results.querySelectorAll('.predictive-search__item');
      const hasExistingItems = existingItems.length > 0;
      const hasNewResults = resultsMarkup && resultsMarkup.trim().length > 0;
      
      // If we have existing items but no new results, fade them out
      if (hasExistingItems && !hasNewResults) {
        existingItems.forEach(item => {
          item.classList.add('fade-out');
        });
        // Wait for fade out animation to complete before clearing
        setTimeout(() => {
          if (this.results) {
            this.results.innerHTML = '';
          }
        }, 200); // Match animation duration
        return;
      }
      
      if (!resultsMarkup) {
        if (typeof window !== 'undefined' && window.console && window.console.error) {
          console.error('Predictive search: Cannot render results - no markup provided');
        }
        return;
      }
      
      if (typeof window !== 'undefined' && window.console && window.console.log) {
        console.log('Predictive search: Rendering results in overlay mode:', this.overlayMode);
        console.log('Results container:', this.results);
      }
      
      // In overlay mode, hide initial content BEFORE rendering results
      if (this.overlayMode) {
          // Hide initial content container directly
          const initialContentContainer = this.overlayContainer && this.overlayContainer.querySelector('[data-initial-content]');
          if (initialContentContainer) {
            initialContentContainer.style.display = 'none';
            initialContentContainer.setAttribute('hidden', '');
            initialContentContainer.hidden = true;
          }
          
          // Also hide via initialContent instance if available
          if (this.initialContent && this.initialContent.container) {
            this.initialContent.hide();
            this.initialContent.container.style.display = 'none';
            this.initialContent.container.setAttribute('hidden', '');
            this.initialContent.container.hidden = true;
          }
          
          // Recent searches removed - no longer used
        }
        
        // Safe: resultsMarkup comes from Shopify's predictive search API
        // In overlay mode, ensure we're using the overlay's results container
        if (this.overlayMode) {
          // Double-check we're using the overlay container, not the standard one
          const overlayResults = (this.overlayContainer && this.overlayContainer.querySelector('[data-predictive-search-results]')) || (this.overlayContainer && this.overlayContainer.querySelector('#predictive-search-overlay-results'));
          if (overlayResults && overlayResults !== this.results) {
            // If we somehow got the wrong container, switch to the correct one
            this.results = overlayResults;
          }
          
          // Hide the standard results container if it exists
          const standardResults = this.querySelector('.js-search-results');
          if (standardResults && standardResults !== this.results) {
            standardResults.style.display = 'none';
            standardResults.hidden = true;
          }
        }
        
      // Clear results first to ensure fresh render
      if (this.results) {
        // Remove all child nodes instead of using innerHTML to preserve any event listeners
        while (this.results.firstChild) {
          this.results.removeChild(this.results.firstChild);
        }
        // Ensure container is ready to show
        this.results.removeAttribute('hidden');
        this.results.hidden = false;
      }

      // Parse HTML and properly attach forms to DOM
      // CRITICAL: Ensure overlay container is in document.body before inserting HTML
      // Forms must be in the document tree to be "connected"
      if (this.overlayMode && this.overlayContainer) {
        // Ensure overlay is in document.body
        if (!this.overlayContainer.isConnected) {
          if (!document.body.contains(this.overlayContainer)) {
            document.body.appendChild(this.overlayContainer);
          }
        }
      }
      
      // CRITICAL: Verify markup before inserting
      const tempVerifyDiv = document.createElement('div');
      tempVerifyDiv.innerHTML = resultsMarkup;
      const tempProductItems = tempVerifyDiv.querySelectorAll('li.predictive-search__item:not(.js-submit)');
      const tempProductCount = tempProductItems.length;
      
      if (typeof window !== 'undefined' && window.console && window.console.log) {
        console.log('🔍 Predictive search: Products in markup BEFORE innerHTML assignment:', tempProductCount);
        console.log('🔍 Predictive search: Product handles in markup:', Array.from(tempProductItems).map(item => {
          const link = item.querySelector('a[href*="/products/"]');
          return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
        }).filter(Boolean));
      }
      
      // Use innerHTML to insert markup
      this.results.innerHTML = resultsMarkup;
      
      // CRITICAL: Immediately verify product count after innerHTML assignment
      const immediateProductItems = this.results.querySelectorAll('li.predictive-search__item:not(.js-submit)');
      const immediateProductCount = immediateProductItems.length;
      
      if (typeof window !== 'undefined' && window.console && window.console.log) {
        console.log('🔍 Predictive search: Products in DOM IMMEDIATELY after innerHTML:', immediateProductCount);
        console.log('🔍 Predictive search: Product handles in DOM immediately:', Array.from(immediateProductItems).map(item => {
          const link = item.querySelector('a[href*="/products/"]');
          return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
        }).filter(Boolean));
      }
      
      // If products were lost, log detailed information
      if (immediateProductCount < tempProductCount) {
        if (typeof window !== 'undefined' && window.console && window.console.error) {
          console.error('🔍 Predictive search: ❌ Products lost during innerHTML assignment!', {
            before: tempProductCount,
            after: immediateProductCount,
            lost: tempProductCount - immediateProductCount
          });
          
          // Find which products are missing
          const tempHandles = Array.from(tempProductItems).map(item => {
            const link = item.querySelector('a[href*="/products/"]');
            return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
          }).filter(Boolean);
          
          const immediateHandles = Array.from(immediateProductItems).map(item => {
            const link = item.querySelector('a[href*="/products/"]');
            return link ? link.href.match(/\/products\/([^?#]+)/)?.[1] : null;
          }).filter(Boolean);
          
          const missingHandles = tempHandles.filter(h => !immediateHandles.includes(h));
          console.error('🔍 Predictive search: Missing product handles:', missingHandles);
        }
      }
      
      // CRITICAL: Force a reflow to ensure DOM is fully updated
      // This helps ensure forms are properly connected
      void this.results.offsetHeight;
      
      // Immediately after innerHTML, verify all forms are connected
      // If any forms are not connected, re-attach them properly
      const allForms = this.results.querySelectorAll('form');
      allForms.forEach(function(form) {
        // Check if form is actually in the document
        if (!form.isConnected || !document.body.contains(form)) {
          // Form is not connected - find its parent and re-attach
          const parent = form.parentNode;
          if (parent && parent.isConnected) {
            // Clone form to create a fresh connection
            const formClone = form.cloneNode(true);
            // Remove old form
            if (form.parentNode) {
              form.parentNode.removeChild(form);
            }
            // Insert cloned form
            parent.appendChild(formClone);
            
            if (typeof window !== 'undefined' && window.console && window.console.log) {
              console.log('Predictive search: Re-attached disconnected form');
            }
          }
        }
      });
      
      // CRITICAL: Verify product count after form re-attachment
      const afterFormCheckItems = this.results.querySelectorAll('li.predictive-search__item:not(.js-submit)');
      const afterFormCheckCount = afterFormCheckItems.length;
      
      if (typeof window !== 'undefined' && window.console && window.console.log) {
        console.log('🔍 Predictive search: Products in DOM after form re-attachment:', afterFormCheckCount);
      }
      
      if (afterFormCheckCount < tempProductCount) {
        if (typeof window !== 'undefined' && window.console && window.console.warn) {
          console.warn('🔍 Predictive search: ⚠️ Products lost during form re-attachment!', {
            before: tempProductCount,
            after: afterFormCheckCount
          });
        }
      }
      
      // Now that HTML is in place, upgrade all custom elements
      // This must happen AFTER appendChild to ensure proper initialization
      if (typeof window !== 'undefined' && window.customElements) {
        // Upgrade tabbed-content first (if present)
        // CRITICAL: Only upgrade if tabs.js is available and element exists
        const tabbedContent = this.results.querySelector('tabbed-content');
        if (tabbedContent && !tabbedContent.hasAttribute('data-upgraded')) {
          try {
            // Check if Tabs class exists before upgrading
            if (typeof window !== 'undefined' && window.customElements && window.customElements.get('tabbed-content')) {
              // CRITICAL: Before upgrading, verify that all tab panels exist and have valid IDs
              // tabs.js will fail if a tab references a panel that doesn't exist
              const tabList = tabbedContent.querySelector('[role="tablist"]');
              const tabs = tabbedContent.querySelectorAll('[role="tab"]');
              const allTabPanels = tabbedContent.querySelectorAll('[role="tabpanel"]');
              
              // Verify that all tabs have corresponding panels
              let allTabsValid = true;
              if (tabs && tabs.length > 0) {
                tabs.forEach(function(tab) {
                  const panelId = tab.getAttribute('aria-controls');
                  if (panelId) {
                    const panel = document.getElementById(panelId);
                    if (!panel) {
                      allTabsValid = false;
                      if (typeof window !== 'undefined' && window.console && window.console.warn) {
                        console.warn('Predictive search: Tab references non-existent panel:', panelId);
                      }
                    }
                  }
                });
              }
              
              // Only upgrade if all tabs are valid, or if there are no tabs (just panels)
              if (allTabsValid || !tabs || tabs.length === 0) {
                // CRITICAL: Before upgrading, ensure all tab panels are visible
                // tabs.js might hide panels during initialization
                allTabPanels.forEach(function(panel) {
                  // If this panel contains products, ensure it's visible
                  if (panel.querySelector('.predictive-search__list') || panel.querySelector('#header-search-products')) {
                    panel.removeAttribute('hidden');
                    panel.hidden = false;
                    panel.style.setProperty('display', 'block', 'important');
                    panel.style.setProperty('visibility', 'visible', 'important');
                    panel.style.setProperty('opacity', '1', 'important');
                  }
                });
                
                window.customElements.upgrade(tabbedContent);
                tabbedContent.setAttribute('data-upgraded', 'true');
                
                // After upgrade, ensure product panel is still visible
                // tabs.js might have hidden it during initialization
                setTimeout(function() {
                  allTabPanels.forEach(function(panel) {
                    if (panel.querySelector('.predictive-search__list') || panel.querySelector('#header-search-products')) {
                      panel.removeAttribute('hidden');
                      panel.hidden = false;
                      panel.style.setProperty('display', 'block', 'important');
                      panel.style.setProperty('visibility', 'visible', 'important');
                      panel.style.setProperty('opacity', '1', 'important');
                    }
                  });
                }, 100);
              } else {
                // Skip upgrade if tabs are invalid - this will prevent the error
                if (typeof window !== 'undefined' && window.console && window.console.warn) {
                  console.warn('Predictive search: Skipping tabbed-content upgrade due to invalid tab references');
                }
                tabbedContent.setAttribute('data-upgraded', 'skipped');
              }
            }
          } catch (e) {
            // Element might already be upgraded or tabs.js might not be loaded
            if (typeof window !== 'undefined' && window.console && window.console.warn) {
              console.warn('Predictive search: Could not upgrade tabbed-content:', e);
            }
            // Mark as attempted to prevent retries
            tabbedContent.setAttribute('data-upgraded', 'failed');
          }
        }
        
        // Then upgrade all product-form elements and initialize them
        // CRITICAL: When HTML is inserted via innerHTML, custom elements exist as plain HTML
        // They need to be upgraded AND init() must be called manually
        requestAnimationFrame(function() {
          const productFormsImmediate = this.results.querySelectorAll('product-form');
          productFormsImmediate.forEach(function(form) {
            if (!form.hasAttribute('data-predictive-search-initialized')) {
              try {
                // Upgrade custom element
                window.customElements.upgrade(form);
                
                // CRITICAL: After upgrade, manually call init() if it exists
                // The constructor only runs when element is first created, not on upgrade
                if (typeof form.init === 'function') {
                  form.init();
                }
              } catch (e) {
                // Element might already be upgraded
                if (typeof window !== 'undefined' && window.console && window.console.warn) {
                  console.warn('Predictive search: Error upgrading product-form:', e);
                }
              }
            }
          });
        }.bind(this));
      }
        
        // In overlay mode, render sidebar and ensure results are visible
        if (this.overlayMode) {
          // Hide initial content FIRST
          const initialContentContainer = this.overlayContainer && this.overlayContainer.querySelector('[data-initial-content]');
          if (initialContentContainer) {
            initialContentContainer.style.setProperty('display', 'none', 'important');
            initialContentContainer.setAttribute('hidden', '');
            initialContentContainer.hidden = true;
          }
          
          if (this.initialContent && this.initialContent.container) {
            this.initialContent.hide();
            this.initialContent.container.style.setProperty('display', 'none', 'important');
            this.initialContent.container.setAttribute('hidden', '');
            this.initialContent.container.hidden = true;
          }
          
          // Hide recent searches
          const recentSearchesInInitial = this.overlayContainer && this.overlayContainer.querySelector('[data-recent-searches-initial]');
          const recentSearchesStandalone = this.overlayContainer && this.overlayContainer.querySelector('[data-recent-searches]');
          
          if (recentSearchesInInitial) {
            recentSearchesInInitial.hidden = true;
            recentSearchesInInitial.style.setProperty('display', 'none', 'important');
          }
          if (recentSearchesStandalone) {
            recentSearchesStandalone.hidden = true;
            recentSearchesStandalone.style.setProperty('display', 'none', 'important');
          }
          
          if (this.recentSearches && this.recentSearches.container) {
            this.recentSearches.container.classList.add('hidden');
            this.recentSearches.container.style.setProperty('display', 'none', 'important');
            this.recentSearches.container.hidden = true;
          }
          
          // Sidebar (real Shopify facets) is populated by fetchFullSearchSection() after render.
          // Reset it hidden here; buildFacetSidebar() reveals it when facets exist for this query.
          {
            const sidebarContainer = this.overlayContainer && this.overlayContainer.querySelector('[data-predictive-search-sidebar-container]');
            if (sidebarContainer) {
              sidebarContainer.hidden = true;
              sidebarContainer.style.setProperty('display', 'none', 'important');
            }
          }
          
          // Also ensure the wrapper is visible
          const resultsWrapper = this.overlayContainer && this.overlayContainer.querySelector('.predictive-search-overlay__results-wrapper');
          
          // Force display of results container - use setProperty to override !important
          if (this.results) {
            this.results.style.setProperty('display', 'flex', 'important'); // Force display with !important
            this.results.removeAttribute('hidden'); // Remove hidden attribute if present
            this.results.hidden = false;
            
            if (resultsWrapper) {
              resultsWrapper.style.setProperty('display', 'flex', 'important');
              resultsWrapper.removeAttribute('hidden');
              resultsWrapper.hidden = false;
            }
            
            // CRITICAL: Force .predictive-search element to be visible
            // The .predictive-search element has display: none by default and only shows when predictive-search[open]
            // In overlay mode, we need to explicitly set it to display: block
            const predictiveSearchEl = this.results.querySelector('.predictive-search');
            if (predictiveSearchEl) {
              predictiveSearchEl.style.setProperty('display', 'block', 'important');
              predictiveSearchEl.style.setProperty('visibility', 'visible', 'important');
              predictiveSearchEl.style.setProperty('opacity', '1', 'important');
              predictiveSearchEl.removeAttribute('hidden');
              predictiveSearchEl.hidden = false;
              
              if (typeof window !== 'undefined' && window.console && window.console.log) {
                console.log('Predictive search: Forced .predictive-search to display: block');
              }
            }
            
            // CRITICAL: Force all product links in #header-search-products to be visible
            // These might have display: none from default theme CSS
            const headerSearchProducts = this.results.querySelector('#header-search-products');
            if (headerSearchProducts) {
              const productLinks = headerSearchProducts.querySelectorAll('ul li a, .predictive-result');
              productLinks.forEach(function(link) {
                link.style.setProperty('display', 'flex', 'important');
                link.style.setProperty('visibility', 'visible', 'important');
                link.style.setProperty('opacity', '1', 'important');
                link.removeAttribute('hidden');
                link.hidden = false;
                
                // Remove query params from product links (e.g., ?_pos=1&_psq=dj&_ss=e&_v=1.0)
                if (link.href && link.href.includes('?')) {
                  const cleanUrl = link.href.split('?')[0];
                  link.href = cleanUrl;
                }
              });
              
              if (typeof window !== 'undefined' && window.console && window.console.log) {
                console.log('Predictive search: Forced', productLinks.length, 'product links to display: flex');
              }
            }
            
            // Also clean all collection and product links in the results
            const allLinks = this.results.querySelectorAll('a[href*="collections/"], a[href*="/products/"]');
            allLinks.forEach(function(link) {
              if (link.href && link.href.includes('?')) {
                // Remove query params like ?_pos=1&_psq=dj&_ss=e&_v=1.0
                const cleanUrl = link.href.split('?')[0];
                link.href = cleanUrl;
              }
            });
            
          // Ensure "Læg i kurv" buttons are visible
          const addToCartButtons = this.results.querySelectorAll('button[name="add"], .btn--primary, [data-add-to-cart-text], .quick-add button, .card__quick-add button');
          addToCartButtons.forEach(function(button) {
            button.style.setProperty('display', 'inline-flex', 'important');
            button.style.setProperty('visibility', 'visible', 'important');
            button.style.setProperty('opacity', '1', 'important');
          });

          // Ensure quick-add containers are visible
          const quickAddContainers = this.results.querySelectorAll('.card__quick-add, .quick-add, product-form');
          quickAddContainers.forEach(function(container) {
            container.style.setProperty('display', 'block', 'important');
            container.style.setProperty('visibility', 'visible', 'important');
            container.style.setProperty('opacity', '1', 'important');
          });

          // Remove "Se detaljer" buttons - match Helloretail
          const viewDetailButtons = this.results.querySelectorAll('.quick-add-view-btn, .btn--secondary.quick-add-view-btn, a[href*="/products/"].btn--secondary');
          viewDetailButtons.forEach(function(button) {
            button.style.setProperty('display', 'none', 'important');
            button.style.setProperty('visibility', 'hidden', 'important');
            button.style.setProperty('opacity', '0', 'important');
            // Also remove from DOM if possible
            if (button.parentNode) {
              button.parentNode.removeChild(button);
            }
          });

          // Initialize product-form web components so "Læg i kurv" buttons work
          // CRITICAL: Wait for tabbed-content to initialize first, then initialize product-forms
          // This ensures tabs.js doesn't fail and break the DOM structure
          
          const initProductForms = function() {
            // First, ensure tabbed-content is properly initialized
            const tabbedContent = this.results.querySelector('tabbed-content');
            if (tabbedContent) {
              // Wait for tabs to be ready
              if (!tabbedContent.hasAttribute('data-tabs-initialized')) {
                try {
                  // Force re-initialization of tabs if needed
                  if (typeof window !== 'undefined' && window.customElements && window.customElements.get('tabbed-content')) {
                    window.customElements.upgrade(tabbedContent);
                  }
                  tabbedContent.setAttribute('data-tabs-initialized', 'true');
                } catch (e) {
                  // Tabs might already be initialized
                }
              }
            }
            
            // Now initialize all product-form elements
            const productForms = this.results.querySelectorAll('product-form');
            if (productForms && productForms.length > 0) {
              // Check if product-form custom element is defined
              if (typeof window !== 'undefined' && window.customElements && window.customElements.get('product-form')) {
                productForms.forEach(function(form) {
                  // Only process if not already initialized
                  if (!form.hasAttribute('data-predictive-search-initialized')) {
                    form.setAttribute('data-predictive-search-initialized', 'true');
                    
                    // Ensure form is connected (should be true after innerHTML)
                    if (!form.isConnected) {
                      if (typeof window !== 'undefined' && window.console && window.console.warn) {
                        console.warn('Predictive search: product-form not connected to DOM');
                      }
                      return; // Skip if not connected
                    }
                    
                    // Force upgrade if not already upgraded
                    try {
                      window.customElements.upgrade(form);
                    } catch (e) {
                      // Element might already be upgraded
                    }
                    
                    // CRITICAL: After upgrade, check if init() was called
                    // When HTML is inserted via innerHTML, custom elements are not automatically initialized
                    // The constructor only calls init() if hasChildNodes() is true at construction time
                    // But when we upgrade an existing element, constructor doesn't run again
                    // So we MUST call init() manually after upgrade
                    if (typeof form.init === 'function') {
                      try {
                        // Call init() - this sets up form event listeners and enables id input
                        form.init();
                        
                        // Verify init worked
                        const formElement = form.querySelector('.js-product-form');
                        if (formElement) {
                          // Mark form as being in overlay - this helps product-form.js detect it
                          formElement.setAttribute('data-in-overlay', 'true');
                          
                          // Check if form has submit listener (init should have added it)
                          const hasSubmitListener = formElement.getAttribute('data-submit-listener') === 'true';
                          if (!hasSubmitListener && typeof window !== 'undefined' && window.console && window.console.log) {
                            console.log('Predictive search: product-form.init() called, form element found');
                          }
                          
                          // After init, add stopPropagation to prevent overlay from closing
                          formElement.addEventListener('submit', function(e) {
                            e.stopPropagation(); // Prevent event bubbling to overlay
                          }, { capture: true });
                          
                          const submitBtn = formElement.querySelector('button[name="add"], button[type="submit"]');
                          if (submitBtn) {
                            submitBtn.addEventListener('click', function(e) {
                              e.stopPropagation(); // Prevent event bubbling to overlay
                            }, { capture: true });
                          }
                        }
                      } catch (e) {
                        if (typeof window !== 'undefined' && window.console && window.console.error) {
                          console.error('Predictive search: Error calling product-form.init():', e);
                        }
                      }
                    } else {
                      // If init() doesn't exist, manually set up the form
                      const formElement = form.querySelector('.js-product-form');
                      if (formElement) {
                        const idInput = formElement.querySelector('[name="id"]');
                        if (idInput && idInput.disabled) {
                          idInput.disabled = false;
                        }
                        
                        // Manually attach submit handler if needed
                        const submitBtn = form.querySelector('[name="add"]');
                        if (submitBtn && theme && theme.settings && theme.settings.afterAtc !== 'no-js') {
                          formElement.addEventListener('submit', function(evt) {
                            evt.preventDefault();
                            // Form submission will be handled by product-form if properly initialized
                          });
                        }
                      }
                    }
                    
                    // Ensure form is visible
                    form.style.setProperty('display', 'block', 'important');
                    form.style.setProperty('visibility', 'visible', 'important');
                    form.style.setProperty('opacity', '1', 'important');
                  }
                });
              }
            }

            // Ensure all form elements are properly connected and functional
            // Find forms both directly and inside product-form custom elements
            const directForms = this.results.querySelectorAll('form[action*="/cart/add"]');
            // Reuse productForms from above (already declared in initProductForms function)
            const productFormsForForms = this.results.querySelectorAll('product-form');
            
            // Check forms inside product-form custom elements
            productFormsForForms.forEach(function(productForm) {
              const form = productForm.querySelector('form.js-product-form');
              if (form) {
                // Verify form is connected
                if (!form.isConnected) {
                  if (typeof window !== 'undefined' && window.console && window.console.warn) {
                    console.warn('Predictive search: Form not connected inside product-form:', form);
                  }
                } else {
                  // Form is connected - ensure it's visible and functional
                  form.style.setProperty('display', 'block', 'important');
                  form.style.setProperty('visibility', 'visible', 'important');
                  form.style.setProperty('opacity', '1', 'important');
                  
                  // Ensure submit button is clickable and has proper styling
                  const submitButton = form.querySelector('button[name="add"], button[type="submit"]');
                  if (submitButton) {
                    submitButton.style.setProperty('pointer-events', 'auto', 'important');
                    submitButton.style.setProperty('cursor', 'pointer', 'important');
                    submitButton.style.setProperty('user-select', 'none', 'important');
                    
                    // Prevent overlay from closing when clicking button
                    submitButton.addEventListener('click', function(e) {
                      e.stopPropagation(); // Prevent event bubbling to overlay
                    }, { capture: true });
                    
                    // Ensure the hidden id input is enabled
                    const idInput = form.querySelector('[name="id"]');
                    if (idInput && idInput.disabled) {
                      idInput.disabled = false;
                    }
                  }
                }
              }
            });
            
            // Also check direct forms (if any)
            directForms.forEach(function(form) {
              // Skip if already processed as part of product-form
              if (form.closest('product-form')) {
                return;
              }
              
              // Verify form is connected
              if (!form.isConnected) {
                if (typeof window !== 'undefined' && window.console && window.console.warn) {
                  console.warn('Predictive search: Form not connected:', form);
                }
              } else {
                // Form is connected - ensure it's visible and functional
                form.style.setProperty('display', 'block', 'important');
                form.style.setProperty('visibility', 'visible', 'important');
                form.style.setProperty('opacity', '1', 'important');
                
                // Ensure submit button is clickable and has proper styling
                const submitButton = form.querySelector('button[name="add"], button[type="submit"]');
                if (submitButton) {
                  submitButton.style.setProperty('pointer-events', 'auto', 'important');
                  submitButton.style.setProperty('cursor', 'pointer', 'important');
                  submitButton.style.setProperty('user-select', 'none', 'important');
                  
                  // Prevent overlay from closing when clicking button
                  submitButton.addEventListener('click', function(e) {
                    e.stopPropagation(); // Prevent event bubbling to overlay
                  }, { capture: true });
                  
                  // Ensure the hidden id input is enabled
                  const idInput = form.querySelector('[name="id"]');
                  if (idInput && idInput.disabled) {
                    idInput.disabled = false;
                  }
                }
              }
            });
          }.bind(this);
          
          // Initialize immediately after DOM is ready
          if (this.results) {
            // First, verify all forms are connected and fix if needed
            const verifyForms = function() {
              const allForms = this.results.querySelectorAll('form[action*="/cart/add"]');
              let fixedCount = 0;
              
              allForms.forEach(function(form) {
                // Check both isConnected and document.body.contains
                const isProperlyConnected = form.isConnected && document.body.contains(form);
                
                if (!isProperlyConnected) {
                  if (typeof window !== 'undefined' && window.console && window.console.warn) {
                    console.warn('Predictive search: Form not properly connected, fixing:', form);
                  }
                  
                  // Try to find the form's intended parent
                  const productForm = form.closest('product-form');
                  if (productForm && productForm.isConnected && document.body.contains(productForm)) {
                    // Clone form with all its content
                    const formClone = form.cloneNode(true);
                    
                    // Remove old form
                    if (form.parentNode) {
                      form.parentNode.removeChild(form);
                    }
                    
                    // Insert cloned form into product-form
                    productForm.appendChild(formClone);
                    fixedCount++;
                    
                    if (typeof window !== 'undefined' && window.console && window.console.log) {
                      console.log('Predictive search: Fixed form connection');
                    }
                  } else {
                    // If no product-form parent, try to find any connected parent
                    let currentParent = form.parentNode;
                    while (currentParent && !currentParent.isConnected) {
                      currentParent = currentParent.parentNode;
                    }
                    
                    if (currentParent && currentParent.isConnected) {
                      const formClone = form.cloneNode(true);
                      if (form.parentNode) {
                        form.parentNode.removeChild(form);
                      }
                      currentParent.appendChild(formClone);
                      fixedCount++;
                    }
                  }
                }
              });
              
              if (fixedCount > 0 && typeof window !== 'undefined' && window.console && window.console.log) {
                console.log('Predictive search: Fixed', fixedCount, 'disconnected forms');
              }
            }.bind(this);
            
            // Use requestAnimationFrame to ensure DOM is fully ready
            requestAnimationFrame(function() {
              verifyForms();
              initProductForms();
              
              // Also try after a short delay to catch any async initialization
              setTimeout(function() {
                verifyForms();
                initProductForms();
              }, 100);
              setTimeout(function() {
                verifyForms();
                initProductForms();
              }, 300);
            });
          }
          
          // Remove "Se detaljer" buttons completely from search results
          const viewButtons = this.results.querySelectorAll('.quick-add-view-btn, a.btn--secondary, a[href*="/products/"].btn--secondary');
          viewButtons.forEach(function(btn) {
            btn.remove(); // Completely remove from DOM
          });
          
          // Also remove "Se detaljer" buttons from initial content if it's visible
          if (this.initialContent && this.initialContent.container) {
            const initialViewButtons = this.initialContent.container.querySelectorAll('.quick-add-view-btn, a.btn--secondary, a[href*="/products/"].btn--secondary');
            initialViewButtons.forEach(function(btn) {
              btn.remove(); // Completely remove from DOM
            });
          }
          
          // Optimize images for fast loading
          const productImages = this.results.querySelectorAll('.predictive-result__media img, .card__media img, .product-card__media img');
          productImages.forEach(function(img) {
            // Set loading attribute for lazy loading
            if (!img.hasAttribute('loading')) {
              img.setAttribute('loading', 'lazy');
            }
            // Set decoding attribute for async decoding
            if (!img.hasAttribute('decoding')) {
              img.setAttribute('decoding', 'async');
            }
            // Ensure proper sizing - show full image without cropping
            img.style.width = 'auto'; // Let image maintain aspect ratio
            img.style.height = '100%'; // Fill container height
            img.style.maxWidth = '100%'; // Don't exceed container width
            img.style.maxHeight = '100%'; // Don't exceed container height
            img.style.objectFit = 'contain'; // Show full image without cropping - fit within container
            img.style.objectPosition = 'center';
            img.style.backgroundColor = '#ffffff'; // White background
            img.style.margin = '0 auto'; // Center horizontally
            // Override absolute positioning from product-card snippet
            img.style.position = 'static';
            img.style.top = 'auto';
            img.style.left = 'auto';
            img.style.right = 'auto';
            img.style.bottom = 'auto';
            // Special handling for download products - fill container completely
            const imgSrc = img.src || '';
            const imgAlt = (img.alt || '').toLowerCase();
            if (imgSrc.includes('download') || imgAlt.includes('download')) {
              img.style.padding = '0'; // No padding to prevent layout shift
              img.style.boxSizing = 'border-box';
              img.style.objectFit = 'cover'; // Fill container completely
              img.style.objectPosition = 'center center'; // Center the image
              // Ensure parent container has no padding
              const mediaContainer = img.closest('.card__media, .predictive-result__media, .product-card__media');
              if (mediaContainer) {
                mediaContainer.style.padding = '0';
                mediaContainer.style.margin = '0 0 12px 0';
              }
              // Add class to card for CSS targeting (better than :has() for browser support)
              const card = img.closest('.card, .predictive-result, .product-card');
              if (card) {
                card.classList.add('download-product');
              }
            }
          });
          
          // Handle products that cannot be purchased: hide buttons and show "Udsolgt" text
          // Check for: 1) out of stock (data-inventory-level="out_of_stock"), 2) disabled buttons, 3) products with no available variants
          const outOfStockItems = this.results.querySelectorAll('[data-inventory-level="out_of_stock"]');
          const disabledButtons = this.results.querySelectorAll('.card__quick-add button[disabled], .card__quick-add button:disabled');
          
          // Process out of stock items
          outOfStockItems.forEach(function(statusEl) {
            // Find the product card/item containing this status
            const productItem = statusEl.closest('.predictive-search__item, .predictive-result, .card');
            if (productItem) {
              // Update the "Læg i kurv" button to show "Udsolgt" instead of removing it
              const quickAdd = productItem.querySelector('.card__quick-add');
              if (quickAdd) {
                const button = quickAdd.querySelector('button[name="add"], button[type="submit"], button');
                if (button) {
                  // Update button text to "Udsolgt"
                  button.textContent = 'Udsolgt';
                  button.disabled = true;
                  button.setAttribute('disabled', 'disabled');
                  // Make it non-clickable
                  button.style.pointerEvents = 'none';
                  button.style.cursor = 'default';
                  // Apply lighter green styling (will be handled by CSS, but set inline as fallback)
                  button.style.backgroundColor = 'rgba(34, 139, 34, 0.2)';
                  button.style.color = 'rgb(34, 139, 34)';
                  button.style.border = 'none';
                  // Remove any icons
                  const icons = button.querySelectorAll('svg, i, .quick-add-btn-icon');
                  icons.forEach(function(icon) {
                    icon.remove();
                  });
                }
              }
              
              // Remove "Udsolgt" text from inventory status - it should only be in the button
              // Remove all inventory status elements for out of stock products
              const allStatusElements = productItem.querySelectorAll('.product-inventory__status');
              allStatusElements.forEach(function(statusEl) {
                statusEl.remove();
              });
            }
          });
          
          // Process disabled buttons (products that cannot be purchased)
          disabledButtons.forEach(function(disabledBtn) {
            // Find the product card/item containing this button
            const productItem = disabledBtn.closest('.predictive-search__item, .predictive-result, .card');
            if (productItem) {
              // Check if it's already handled (out of stock)
              const isOutOfStock = productItem.querySelector('[data-inventory-level="out_of_stock"]');
              if (!isOutOfStock) {
                // Hide the button container
                const quickAdd = productItem.querySelector('.card__quick-add');
                if (quickAdd) {
                  quickAdd.remove(); // Completely remove from DOM
                }
                
                // Add "Udsolgt" text - ensure it matches other inventory status styling
                const cardTitle = productItem.querySelector('.card__title');
                const cardInfo = productItem.querySelector('.card__info');
                if (cardTitle || cardInfo) {
                  // First, check if there's already a status element
                  const existingStatus = productItem.querySelector('.product-inventory__status');
                  if (!existingStatus) {
                    const udsolgtEl = document.createElement('div');
                    udsolgtEl.className = 'product-inventory__status';
                    udsolgtEl.setAttribute('data-inventory-level', 'out_of_stock');
                    udsolgtEl.textContent = 'Udsolgt';
                    udsolgtEl.style.color = 'rgb(220, 53, 69)';
                    udsolgtEl.style.fontSize = '14px';
                    udsolgtEl.style.fontWeight = '400';
                    udsolgtEl.style.marginTop = '0';
                    udsolgtEl.style.marginBottom = '0';
                    udsolgtEl.style.paddingTop = '0';
                    udsolgtEl.style.paddingBottom = '0';
                    udsolgtEl.style.minHeight = 'auto';
                    udsolgtEl.style.lineHeight = 'normal';
                    
                    // Insert after title if it exists, otherwise at start of card__info
                    if (cardTitle && cardTitle.parentNode) {
                      cardTitle.parentNode.insertBefore(udsolgtEl, cardTitle.nextSibling);
                    } else if (cardInfo) {
                      cardInfo.insertBefore(udsolgtEl, cardInfo.firstChild);
                    }
                  }
                }
              }
            }
          });
          
          // After rendering predictive results, fetch the full /search section ONCE per
          // settled query: it yields (a) more product cards and (b) the real Shopify facets.
          // Overlay only. Deduped by query key so keystrokes never stack fetches (INP-safe).
          const productCount = this.results.querySelectorAll('li.predictive-search__item:not(.js-submit)').length;
          if (this.overlayMode && productCount >= 1) {
            const fsKey = this.getQuery().toLowerCase().trim();
            if (this._fullSearchKey !== fsKey) {
              this._fullSearchKey = fsKey;
              this.fetchFullSearchSection(this.getQuery()).then(() => {
                this.adjustTitleHeights();
              }).catch(err => {
                if (typeof window !== 'undefined' && window.console && window.console.warn) {
                  console.warn('🔍 Predictive search: Error fetching full search section:', err);
                }
              });
            }
          }
          
          // Adjust title heights after initial render
          // Use requestAnimationFrame to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            // Wait for images to load before adjusting heights
            this.waitForImages().then(() => {
              this.adjustTitleHeights();
            }).catch(() => {
              // If images fail to load, adjust anyway after a short delay
              setTimeout(() => {
                this.adjustTitleHeights();
              }, 100);
            });
          });
        }
          
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            // Count actual product items in the rendered results
            const productItems = this.results.querySelectorAll('li.predictive-search__item:not(.js-submit)');
            const productCards = this.results.querySelectorAll('.predictive-search-overlay__product-card');
            const productLinks = this.results.querySelectorAll('a[href*="/products/"]');
            
            console.log('Predictive search: Results rendered, display:', this.results && this.results.style.display);
            console.log('Predictive search: Results innerHTML length:', this.results && this.results.innerHTML.length);
            console.log('Predictive search: Initial content hidden:', initialContentContainer && initialContentContainer.hidden);
            console.log('Predictive search: Results wrapper display:', resultsWrapper && resultsWrapper.style.display);
            console.log('Predictive search: Product items in DOM:', productItems.length);
            console.log('Predictive search: Product cards in DOM:', productCards.length);
            console.log('Predictive search: Product links in DOM:', productLinks.length);
            
            // Also check the list container BEFORE checking visibility
            const productsList = this.results.querySelector('.predictive-search__list, ul');
            if (productsList) {
              const listStyle = window.getComputedStyle(productsList);
              const allListItems = productsList.querySelectorAll('li');
              const productListItems = productsList.querySelectorAll('li.predictive-search__item:not(.js-submit)');
              const viewAllButton = productsList.querySelector('li.js-submit');
              
              console.log('Predictive search: Products list analysis:');
              console.log('  - All list items (including .js-submit):', allListItems.length);
              console.log('  - Product items (excluding .js-submit):', productListItems.length);
              console.log('  - View all button:', viewAllButton ? 'found' : 'not found');
              console.log('  - List styles:', {
                display: listStyle.display,
                visibility: listStyle.visibility,
                opacity: listStyle.opacity,
                width: listStyle.width,
                maxWidth: listStyle.maxWidth,
                flexWrap: listStyle.flexWrap,
                childrenCount: productsList.children.length
              });
              
              // Log each item in the list
              console.log('  - List items breakdown:');
              Array.from(allListItems).forEach((item, index) => {
                const isViewAll = item.classList.contains('js-submit');
                const hasProductCard = !!item.querySelector('.predictive-search-overlay__product-card');
                const hasProductLink = !!item.querySelector('a[href*="/products/"]');
                const itemStyle = window.getComputedStyle(item);
                const productHandle = item.querySelector('a[href*="/products/"]')?.href.match(/\/products\/([^?#]+)/)?.[1];
                
                console.log(`    Item ${index + 1}:`, {
                  isViewAll: isViewAll,
                  hasProductCard: hasProductCard,
                  hasProductLink: hasProductLink,
                  productHandle: productHandle || 'N/A',
                  display: itemStyle.display,
                  visibility: itemStyle.visibility,
                  opacity: itemStyle.opacity,
                  offsetWidth: item.offsetWidth,
                  offsetHeight: item.offsetHeight,
                  classes: item.className
                });
              });
            }
            
            // Check if any items are hidden by CSS
            let visibleItems = 0;
            let hiddenItems = 0;
            const hiddenItemsList = [];
            productItems.forEach((item, index) => {
              const style = window.getComputedStyle(item);
              const isHidden = style.display === 'none' || 
                              style.visibility === 'hidden' || 
                              style.opacity === '0' || 
                              style.height === '0px' ||
                              item.offsetWidth === 0 ||
                              item.offsetHeight === 0;
              
              if (isHidden) {
                hiddenItems++;
                hiddenItemsList.push({
                  index: index,
                  display: style.display,
                  visibility: style.visibility,
                  opacity: style.opacity,
                  width: style.width,
                  height: style.height,
                  offsetWidth: item.offsetWidth,
                  offsetHeight: item.offsetHeight,
                  classes: item.className
                });
                
                // Force visibility for hidden items
                item.style.setProperty('display', 'flex', 'important');
                item.style.setProperty('visibility', 'visible', 'important');
                item.style.setProperty('opacity', '1', 'important');
                item.style.setProperty('width', 'auto', 'important');
                item.style.setProperty('height', 'auto', 'important');
              } else {
                visibleItems++;
              }
            });
            console.log('Predictive search: Visible product items:', visibleItems);
            console.log('Predictive search: Hidden product items:', hiddenItems);
            if (hiddenItems > 0) {
              console.warn('Predictive search: Hidden items details:', hiddenItemsList);
            }
          }
        }
      
      this.setAttribute('results', true);
      this.open();
      
      // Save search term to recent searches when results are shown
      const searchTerm = this.getQuery();
      if (this.overlayMode && this.recentSearches && searchTerm.length > 0) {
        // Don't save immediately, wait for user to click a result
        const resultLinks = this.results && this.results.querySelectorAll('.js-search-link, .predictive-result');
        if (resultLinks) {
          resultLinks.forEach(link => {
            link.addEventListener('click', () => {
              this.recentSearches.saveRecentSearchTerm(searchTerm);
            }, { once: true });
          });
        }
      }
    }

    /**
     * Opens the overlay (for overlay mode)
     */
    openOverlay() {
      if (!this.overlayMode || !this.overlayContainer) return;
      
      this.overlayContainer.classList.add('is-open');
      this.overlayContainer.removeAttribute('hidden');
      this.overlayContainer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('predictive-search-overlay-open');
      document.documentElement.classList.add('predictive-search-overlay-open'); // Also add to html element
      this.isOverlayOpen = true;

      // Pre-warm the collections fuzzy index while the user starts typing (idle, cached).
      const warm = () => { this.ensureCollectionsIndex().catch(() => {}); };
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 });
      else setTimeout(warm, 500);
      
      // Focus main input (header search field)
      if (this.input) {
        this.input.focus();
      }
      
      // Trap focus within overlay for accessibility
      this.setupFocusTrap();
      
      // Show initial content if no search term
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        // Hide results and results-wrapper
        if (this.results) {
          this.results.innerHTML = '';
          this.results.style.display = 'none';
        }
        
        // CRITICAL: Hide results-wrapper when no search term
        const resultsWrapper = this.overlayContainer && this.overlayContainer.querySelector('.predictive-search-overlay__results-wrapper');
        if (resultsWrapper) {
          resultsWrapper.style.setProperty('display', 'none', 'important');
          resultsWrapper.setAttribute('hidden', '');
          resultsWrapper.hidden = true;
        }
        
        // Usually a no-op paint: the markup was prefetched on the focus that
        // triggered lazy init, so it is already in the DOM by the time we open.
        this.revealInitialContent();
        // Recent searches removed - no longer used
      } else {
        // Hide initial content and recent searches when there's a search term
        if (this.initialContent && this.initialContent.container) {
          this.initialContent.hide();
          this.initialContent.container.style.display = 'none';
        }
        if (this.results) {
          this.results.style.display = 'flex'; // Use flex to match CSS
        }
        // Recent searches removed - no longer used
      }
    }
    
    /**
     * Closes the overlay (for overlay mode)
     */
    closeOverlay() {
      if (!this.overlayMode || !this.overlayContainer) return;
      
      this.overlayContainer.classList.remove('is-open');
      this.overlayContainer.setAttribute('hidden', '');
      this.overlayContainer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('predictive-search-overlay-open');
      document.documentElement.classList.remove('predictive-search-overlay-open'); // Also remove from html element
      this.isOverlayOpen = false;
      
      // Clear main input
      if (this.input) {
        this.input.value = '';
        this.input.blur();
      }
      
      // Hide content
      if (this.initialContent) {
        this.initialContent.hide();
      }
      // Recent searches removed - no longer used
      if (this.results) {
        this.results.innerHTML = '';
      }
    }
    
    /**
     * Sets up focus trap for overlay (accessibility)
     */
    setupFocusTrap() {
      if (!this.overlayMode || !this.overlayContainer) return;
      
      // Store previously focused element
      this.previouslyFocusedElement = document.activeElement;
      
      // Find all focusable elements within overlay
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ');
      
      this.focusableElements = Array.from(
        this.overlayContainer.querySelectorAll(focusableSelectors)
      ).filter(el => {
        // Filter out hidden elements
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });
      
      // Setup keyboard trap
      this.focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;
        
        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];
        
        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            if (lastElement && typeof lastElement.focus === 'function') {
              lastElement.focus();
            }
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            if (firstElement && typeof firstElement.focus === 'function') {
              firstElement.focus();
            }
          }
        }
      };
      
      this.overlayContainer.addEventListener('keydown', this.focusTrapHandler);
    }

    /**
     * Opens the results listbox.
     */
    open() {
      if (this.overlayMode) {
        this.openOverlay();
      } else {
        // Original dropdown behavior
        if (this.overlay) {
      this.overlay.classList.add('is-visible');
        }
      }
      
      if (this.getQuery().length && this.resetBtn) {
        this.resetBtn.hidden = false;
      }
      if (this.input) {
      this.input.setAttribute('aria-expanded', 'true');
        // Update aria-haspopup for overlay mode
        if (this.overlayMode) {
          this.input.setAttribute('aria-haspopup', 'dialog');
        }
      }
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
        // Overlay click handler - only close if clicking on overlay background, not on content
        this.overlayClickHandler = this.overlayClickHandler || ((evt) => {
          // Don't close if clicking on:
          // - Forms or buttons (including "Læg i kurv" buttons)
          // - Product cards
          // - Results container
          // - Sidebar
          // - Any interactive element
          const target = evt.target;
          if (target.closest('form') || 
              target.closest('button') || 
              target.closest('.predictive-search-overlay__results') ||
              target.closest('.predictive-search-overlay__sidebar-container') ||
              target.closest('.card__quick-add') ||
              target.closest('product-form') ||
              target.closest('.predictive-result') ||
              target.closest('.predictive-search__item') ||
              target.tagName === 'BUTTON' ||
              target.tagName === 'FORM' ||
              target.tagName === 'INPUT' ||
              target.tagName === 'SELECT' ||
              target.tagName === 'TEXTAREA' ||
              target.closest('a')) {
            return; // Don't close overlay
          }
          // Only close if clicking directly on overlay background
          if (target === this.overlay || target.classList.contains('predictive-search-overlay')) {
            this.close();
          }
        });

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      if (this.resetBtn) {
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      }
      if (this.overlay) {
      this.overlay.addEventListener('click', this.overlayClickHandler);
      }
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      if (this.overlayMode) {
        this.closeOverlay();
      }
      
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn && this.input) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      if (this.resetBtn) {
      this.resetBtn.hidden = true;
      }
      if (this.overlay && !this.overlayMode) {
      this.overlay.classList.remove('is-visible');
      }
      if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
        // Reset aria-haspopup for overlay mode
        if (this.overlayMode) {
          this.input.setAttribute('aria-haspopup', 'dialog');
        } else {
          this.input.setAttribute('aria-haspopup', 'listbox');
        }
      }
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
    }


/**
 * Fetches the full /search section fragment (section_id=main-search): yields more product cards
 * than predictive's 10-cap AND the real Shopify facet markup (#facets). Overlay only.
 * @param {string} searchTerm - Search query
 * @returns {Promise<void>}
 */
async fetchFullSearchSection(searchTerm) {
  try {
    const existingResultsEl = this.results;
    if (!searchTerm || !existingResultsEl) return;

    const searchUrl = this.getLocalizedSearchUrl();

    const searchParams = new URLSearchParams();
    searchParams.set('q', searchTerm);
    searchParams.set('options[prefix]', 'last');
    searchParams.set('type', 'product');
    // section_id=main-search => only the main-search section fragment (facets + #filter-results),
    // lighter than the full page document. (Section key 'main' returns empty; the section
    // TYPE 'main-search' is what the Section Rendering API resolves here.)
    searchParams.set('section_id', 'main-search');

    // Abort any previous in-flight full-search fetch (newer query wins).
    if (this._fullSearchAbort) { try { this._fullSearchAbort.abort(); } catch (e) {} }
    this._fullSearchAbort = new AbortController();

    // Show the stock toggle immediately + skeleton placeholders for the facets so the row
    // doesn't pop in after the fetch.
    this._showFacetSkeleton();

    // Fetch matching collections for the sidebar in parallel (predictive suggest).
    const collectionsPromise = this.fetchSidebarCollections(searchTerm);

    // Keep cookies / localization context
    const response = await fetch(`${searchUrl}?${searchParams.toString()}`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: this._fullSearchAbort.signal
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const responseText = await response.text();
    if (!responseText || responseText.trim().length === 0) return;

    // Parse the section fragment ONCE.
    const tmpl = document.createElement('template');
    tmpl.innerHTML = responseText;

    // Build the sidebar (collections list + real Shopify facets) from the same parse.
    const sidebarCollections = await collectionsPromise.catch(() => []);
    this.buildFacetSidebar(tmpl.content, sidebarCollections);

    // Find products grid (multiple fallbacks)
    let productsGrid =
      tmpl.content.querySelector('.main-products-grid__results') ||
      tmpl.content.querySelector('#filter-results') ||
      tmpl.content.querySelector('.products-grid') ||
      tmpl.content.querySelector('[class*="products-grid"]');

    if (!productsGrid) {
      const section = tmpl.content.querySelector('#shopify-section-main-search, [id*="main-search"]');
      if (section) {
        productsGrid = section.querySelector('.main-products-grid__results, #filter-results, .products-grid');
      }
    }

    if (!productsGrid) return;

    // Find product cards (multiple fallbacks)
    let productCards = productsGrid.querySelectorAll('article[data-product-id]');
    if (productCards.length === 0) productCards = productsGrid.querySelectorAll('.product-card');
    if (productCards.length === 0) productCards = productsGrid.querySelectorAll('.card');
    if (productCards.length === 0) productCards = productsGrid.querySelectorAll('[data-product-id]');

    let cardList = Array.from(productCards);

    // Last resort: build cards from product links
    if (cardList.length === 0) {
      const productLinks = productsGrid.querySelectorAll('a[href*="/products/"]');
      const cardSet = new Set();
      productLinks.forEach((link) => {
        const card =
          link.closest('article, .product-card, .card, li, div[class*="product"]') || link.parentElement;
        if (card) cardSet.add(card);
      });
      cardList = Array.from(cardSet);
    }

    if (cardList.length === 0) return;

    // Find products list in overlay
    let productsList = existingResultsEl.querySelector('.predictive-search__list');
    if (!productsList) productsList = existingResultsEl.querySelector('ul');
    if (!productsList) return;
    const viewAllButton = productsList.querySelector('li.js-submit');

    const handleFromEl = (el) => {
      const link = el && el.querySelector ? el.querySelector('a[href*="/products/"]') : null;
      return link ? (link.href.match(/\/products\/([^?#]+)/)?.[1]?.toLowerCase() || null) : null;
    };

    // Lead with the full-search relevance order (guitars-first). Native /search returns the good
    // ordering; predictive's instant results are mostly accessories (strings, straps, bags), so
    // drop the predictive items this full-search batch also contains (its version leads) and keep
    // predictive-only extras (fuzzy/SKU matches /search missed) — they fall in AFTER, at the first
    // survivor. Only reached when the full-search batch has results, so predictive is preserved
    // untouched when native /search returns nothing (elguitar-fuzzy, EAN, etc.).
    const batchHandles = new Set();
    cardList.forEach((c) => { const h = handleFromEl(c); if (h) batchHandles.add(h); });
    let leadAnchor = null;
    productsList.querySelectorAll('li.predictive-search__item:not(.js-submit)').forEach((li) => {
      const h = handleFromEl(li);
      if (h && batchHandles.has(h)) li.remove();
      else if (!leadAnchor) leadAnchor = li;
    });

    // Existing handles (after the dedupe-removal above) so we never re-add one.
    const existingHandles = new Set();
    existingResultsEl.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const handle = link.href.match(/\/products\/([^?#]+)/)?.[1];
      if (handle) existingHandles.add(handle.toLowerCase());
    });

    const insertRef = leadAnchor || viewAllButton;
    let addedCount = 0;

    // IMPORTANT: use for..of so we can break (forEach + return doesn't stop)
    for (const card of cardList) {
      const productLink = card.querySelector?.('a[href*="/products/"]');
      if (!productLink) continue;

      const handle = productLink.href.match(/\/products\/([^?#]+)/)?.[1];
      if (!handle) continue;

      const handleKey = handle.toLowerCase();
      if (existingHandles.has(handleKey)) continue;

      existingHandles.add(handleKey);

      const newItem = this._buildOverlayCardItem(card);
      if (!newItem) continue;

      // Insert in full-search order, ahead of any predictive-only extras / "View all".
      if (insertRef && insertRef.parentNode === productsList) productsList.insertBefore(newItem, insertRef);
      else productsList.appendChild(newItem);

      addedCount++;

      // Cap high enough to include the whole /search page (products_per_page) after dedupe.
      if (addedCount >= 50) break;
    }

    if (addedCount <= 0) return;

    this._afterOverlayCardsInserted(existingResultsEl, productsList);

    // Track pagination so load-more-on-scroll continues from page 1 for this query. No facet
    // filter is active for a fresh query, so clear any stored filter params.
    this._overlayPageQuery = searchTerm;
    this._overlayLoadedPage = 1;
    this._overlayNoMore = false;
    this._overlayFilterParams = null;

  } catch (error) {
    if (typeof window !== 'undefined' && window.console && window.console.error) {
      console.error('🔍 Predictive search: Error fetching more results from full Search API:', error);
    }
  }
}

    /**
     * Clones a /search product card into a predictive-overlay <li>. Shared by the initial
     * full-search insert and load-more-on-scroll. Returns the <li> (or null).
     */
    _buildOverlayCardItem(card) {
      let cardWrapper = card;
      if (card.tagName !== 'ARTICLE' && card.tagName !== 'DIV') {
        cardWrapper = card.closest('article, .product-card, .card, li') || card;
      }
      const clonedCard = cardWrapper.cloneNode(true);
      clonedCard.querySelectorAll('a[href*="/products/"]').forEach((link) => {
        if (link.href && link.href.includes('?')) link.href = link.href.split('?')[0];
      });
      const newItem = document.createElement('li');
      newItem.className = 'predictive-search__item';
      newItem.setAttribute('role', 'option');
      const wrapper = document.createElement('div');
      wrapper.className = 'predictive-search-overlay__product-card';
      if (clonedCard.classList && clonedCard.classList.contains('card')) {
        wrapper.appendChild(clonedCard);
      } else {
        const cardElement = clonedCard.querySelector('.card, .product-card');
        if (cardElement) {
          wrapper.appendChild(cardElement);
        } else {
          const cardDiv = document.createElement('div');
          cardDiv.className = 'card';
          cardDiv.appendChild(clonedCard);
          wrapper.appendChild(cardDiv);
        }
      }
      newItem.appendChild(wrapper);
      newItem.style.setProperty('display', 'flex', 'important');
      newItem.style.setProperty('flex-direction', 'column', 'important');
      newItem.style.setProperty('visibility', 'visible', 'important');
      newItem.style.setProperty('opacity', '1', 'important');
      newItem.removeAttribute('hidden');
      newItem.hidden = false;
      return newItem;
    }

    /**
     * Post-processing after overlay product cards are inserted: (re)initialise product forms,
     * force visibility, strip "view details" buttons, re-apply the active stock filter. Shared by
     * the initial full-search insert and load-more.
     */
    _afterOverlayCardsInserted(existingResultsEl, productsList) {
      const productForms = existingResultsEl.querySelectorAll('product-form');
      if (productForms && productForms.length > 0 && window.customElements && window.customElements.get('product-form')) {
        productForms.forEach((form) => {
          if (!form.hasAttribute('data-predictive-search-initialized')) {
            form.setAttribute('data-predictive-search-initialized', 'true');
            if (form.isConnected) {
              try {
                window.customElements.upgrade(form);
                if (typeof form.init === 'function') {
                  form.init();
                  const formElement = form.querySelector('.js-product-form');
                  if (formElement) formElement.setAttribute('data-in-overlay', 'true');
                }
              } catch (e) { /* ignore */ }
            }
          }
        });
      }
      if (productsList) {
        productsList.querySelectorAll('li.predictive-search__item:not(.js-submit)').forEach((item) => {
          item.style.setProperty('display', 'flex', 'important');
          item.style.setProperty('visibility', 'visible', 'important');
          item.style.setProperty('opacity', '1', 'important');
          item.removeAttribute('hidden');
          item.hidden = false;
        });
      }
      existingResultsEl
        .querySelectorAll('.quick-add-view-btn, a.btn--secondary, a[href*="/products/"].btn--secondary')
        .forEach((btn) => btn.remove());
      this.applyStockFilter();
    }

    /**
     * Load-more-on-scroll: fetches the next /search page (section_id=main-search&page=N) and
     * appends its product cards after the current ones, in relevance order. Stops when a page
     * returns nothing (or a hard page cap). Overlay only.
     */
    async loadMoreOverlayResults() {
      if (!this.overlayMode) return;
      if (this._overlayLoadingMore || this._overlayNoMore) return;
      const existingResultsEl = this.results;
      if (!existingResultsEl) return;
      const searchTerm = (typeof this.getQuery === 'function' ? (this.getQuery() || '') : '').trim();
      if (!searchTerm || searchTerm !== this._overlayPageQuery) return;

      const nextPage = (this._overlayLoadedPage || 1) + 1;
      if (nextPage > 15) { this._overlayNoMore = true; return; } // hard cap (~15 pages of results)

      this._overlayLoadingMore = true;
      try {
        // Carry the active facet filters (if any) into every page fetch so the loaded-more products
        // respect the filter. Falls back to a plain query when no facet filter is active.
        const params = this._overlayFilterParams
          ? new URLSearchParams(this._overlayFilterParams.toString())
          : new URLSearchParams();
        if (!params.has('q')) params.set('q', searchTerm);
        if (!params.has('options[prefix]')) params.set('options[prefix]', 'last');
        if (!params.has('type')) params.set('type', 'product');
        params.set('section_id', 'main-search');
        params.set('page', String(nextPage));
        const res = await fetch(`${this.getLocalizedSearchUrl()}?${params.toString()}`, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!res.ok) { this._overlayNoMore = true; return; }
        const txt = await res.text();
        const tmpl = document.createElement('template');
        tmpl.innerHTML = txt;
        const grid =
          tmpl.content.querySelector('.main-products-grid__results') ||
          tmpl.content.querySelector('#filter-results') ||
          tmpl.content.querySelector('.products-grid') ||
          tmpl.content.querySelector('[class*="products-grid"]');
        if (!grid) { this._overlayNoMore = true; return; }
        const cards = Array.from(grid.querySelectorAll('article[data-product-id], .product-card, .card, [data-product-id]'));
        if (cards.length === 0) { this._overlayNoMore = true; return; }

        const productsList = existingResultsEl.querySelector('.predictive-search__list') || existingResultsEl.querySelector('ul');
        if (!productsList) { this._overlayNoMore = true; return; }
        const viewAllButton = productsList.querySelector('li.js-submit');

        const existingHandles = new Set();
        existingResultsEl.querySelectorAll('a[href*="/products/"]').forEach((link) => {
          const h = link.href.match(/\/products\/([^?#]+)/)?.[1];
          if (h) existingHandles.add(h.toLowerCase());
        });

        let added = 0;
        for (const card of cards) {
          const link = card.querySelector && card.querySelector('a[href*="/products/"]');
          const handle = link && link.href.match(/\/products\/([^?#]+)/)?.[1]?.toLowerCase();
          if (!handle || existingHandles.has(handle)) continue;
          existingHandles.add(handle);
          const item = this._buildOverlayCardItem(card);
          if (!item) continue;
          if (viewAllButton && viewAllButton.parentNode === productsList) productsList.insertBefore(item, viewAllButton);
          else productsList.appendChild(item);
          added++;
        }

        if (added <= 0) { this._overlayNoMore = true; return; }
        this._overlayLoadedPage = nextPage;
        this._afterOverlayCardsInserted(existingResultsEl, productsList);
      } catch (e) {
        // transient (network/abort): leave _overlayNoMore false so a later scroll can retry.
      } finally {
        this._overlayLoadingMore = false;
      }
    }

    /**
     * Returns the /search URL for the current market/locale (e.g. /da/search, /search).
     */
    getLocalizedSearchUrl() {
      const path = window.location.pathname || '/';
      const firstSegment = path.split('/').filter(Boolean)[0];
      const looksLikeLocale =
        typeof firstSegment === 'string' &&
        firstSegment.length >= 2 &&
        firstSegment.length <= 5 &&
        /^[a-zA-Z-]+$/.test(firstSegment);
      return looksLikeLocale ? `/${firstSegment}/search` : '/search';
    }

    /**
     * Neutralizes a facet fragment BEFORE insertion into the live DOM:
     *  - rename <facet-filters>/<price-range> so their custom-element JS never upgrades
     *    (avoids SideDrawer scroll-lock + global-id slider binding)
     *  - prefix every id + rewrite matching for/aria-controls/aria-labelledby/list so overlay
     *    controls never resolve to the underlying page's facet ids
     *  - strip <script>/<link> so nothing re-executes/re-fetches
     * Operate on a node inside a <template>.content (inert -> no upgrade happens here).
     */
    neutralizeFacetMarkup(node) {
      if (!node) return node;
      const rename = (el, cls) => {
        const div = document.createElement('div');
        if (cls) div.className = cls;
        for (const a of Array.from(el.attributes)) {
          try { div.setAttribute(a.name, a.value); } catch (e) {}
        }
        div.setAttribute('data-was-' + el.tagName.toLowerCase(), '');
        while (el.firstChild) div.appendChild(el.firstChild);
        el.replaceWith(div);
        return div;
      };
      node.querySelectorAll('facet-filters').forEach((el) => rename(el, 'pso-facets'));
      node.querySelectorAll('price-range').forEach((el) => rename(el, ''));
      // details-disclosure's constructor (main.js) reads nextElementSibling and throws on our
      // injected markup; rename it so it never upgrades. Native <details> handles the toggle.
      node.querySelectorAll('details-disclosure').forEach((el) => rename(el, ''));
      node.querySelectorAll('script, link').forEach((el) => el.remove());
      // Show ALL facet values (e.g. every brand) with no "Vis mere" button: reveal any collapsed
      // items and drop the show-more toggle. (The price "Ryd"/.js-clear-filter is separate and kept.)
      node.querySelectorAll('.js-show-more').forEach((el) => el.remove());
      node.querySelectorAll('.js-hidden').forEach((el) => { el.classList.remove('js-hidden'); el.removeAttribute('hidden'); });
      // Collapse all facet groups so they behave as click-to-open dropdowns in the top bar
      // (Shopify's expand_filters setting renders them [open] by default). Shared name makes
      // them an exclusive accordion (only one open at a time) so panels never overlap.
      node.querySelectorAll('details').forEach((d) => {
        d.removeAttribute('open');
        if (d.classList.contains('filter')) d.setAttribute('name', 'pso-facet-group');
      });
      const idMap = {};
      node.querySelectorAll('[id]').forEach((el) => {
        const oldId = el.getAttribute('id');
        if (!oldId || oldId.indexOf('pso-') === 0) return;
        const newId = 'pso-' + oldId;
        idMap[oldId] = newId;
        el.setAttribute('id', newId);
      });
      const remap = (attr) => {
        node.querySelectorAll('[' + attr + ']').forEach((el) => {
          const val = el.getAttribute(attr);
          if (val && idMap[val]) el.setAttribute(attr, idMap[val]);
        });
      };
      remap('for');
      remap('aria-controls');
      remap('aria-labelledby');
      remap('list');
      return node;
    }

    /**
     * Fetches matching collections for the sidebar via the predictive suggest endpoint.
     * @param {string} term - search query
     * @returns {Promise<Array<{title:string,url:string}>>}
     */
    async fetchSidebarCollections(term) {
      // 1) Native predictive suggest (relevance-ranked).
      let suggested = [];
      try {
        const params = new URLSearchParams();
        params.set('q', term);
        params.set('resources[type]', 'collection');
        params.set('resources[limit]', '10');
        // getLocalizedSearchUrl() => '/search' or '/da/search'; suggest lives beside it.
        const url = `${this.getLocalizedSearchUrl()}/suggest.json?${params.toString()}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          const cols = (data.resources && data.resources.results && data.resources.results.collections) || [];
          suggested = cols
            .map((c) => ({ title: c.title, url: (c.url || '').split('?')[0] }))
            .filter((c) => c.title && c.url);
        }
      } catch (e) { /* non-fatal */ }

      // 2) Fuzzy / de-compounding fallback from the client-side collections index.
      // Catches concatenated queries Shopify tokenization misses (e.g. "elguitar" -> "el-guitar").
      let fuzzy = [];
      try {
        await this.ensureCollectionsIndex();
        fuzzy = this.fuzzyCollectionMatches(term);
      } catch (e) { /* non-fatal */ }

      // Merge suggest + fuzzy, dedupe by url.
      const seen = new Set();
      const merged = [];
      [...suggested, ...fuzzy].forEach((c) => {
        if (c && c.title && c.url && !seen.has(c.url)) { seen.add(c.url); merged.push(c); }
      });

      // Re-rank so CATEGORY collections surface ahead of accessory/bundle ones. Shopify's collection
      // suggest ranks e.g. "Guitar Ophæng"/"Guitar pakke" above "El-guitar" for "guitar", and the
      // 10-cap then drops the fuzzy "El-guitar" entirely. In Danish the category noun is usually LAST
      // ("El-guitar", "Begynder Guitar"), while accessory/bundle collections put it FIRST ("Guitar
      // Ophæng"). Rank: exact match, then title ends-with the query (category), then starts-with,
      // then contains — shorter (normalized) titles first within each tier.
      const q = this._normText(term);
      const rank = (title) => {
        const n = this._normText(title);
        if (q && n === q) return 0;
        if (q && n.endsWith(q)) return 1;
        if (q && n.startsWith(q)) return 2;
        return 3;
      };
      merged.sort((a, b) =>
        rank(a.title) - rank(b.title) ||
        this._normText(a.title).length - this._normText(b.title).length
      );
      return merged.slice(0, 12);
    }

    /** Normalize text for fuzzy matching: lowercase, strip non-alphanumerics (keep æøå). */
    _normText(s) {
      return (s || '').toLowerCase().replace(/[^a-z0-9æøå]/g, '');
    }

    /**
     * Keeps only the Price + Brand (vendor) facet groups; removes all others
     * (availability, sort, colour, metafield facets, etc.) per requirement.
     * @param {Element} wrapper - container holding the neutralized #facets markup
     */
    _pruneFacetGroups(wrapper) {
      if (!wrapper) return;
      wrapper.querySelectorAll('.facets__filters .filter').forEach((f) => {
        const keep = f.querySelector('input[name*="price"], input[name="filter.p.vendor"], input[name*="vendor"]');
        if (!keep) f.remove();
      });
    }

    /**
     * Lazily loads (and caches) a compact index of every collection {title,url,norm}
     * via the collections-index section, for fuzzy/de-compounding matching.
     * @returns {Promise<Array<{t:string,u:string,n:string}>>}
     */
    ensureCollectionsIndex() {
      if (this._colIndex) return Promise.resolve(this._colIndex);
      const root = this.getLocalizedSearchUrl().replace(/\/search$/, '');
      // Locale-scoped cache key so a multi-locale domain (e.g. .com with /da/ and /en/)
      // never serves one market's collections index to another. ccTLD markets are separate
      // domains, so sessionStorage is already isolated there.
      const cacheKey = 'pso_col_idx_v1:' + (root || '/');
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) { this._colIndex = JSON.parse(cached); return Promise.resolve(this._colIndex); }
      } catch (e) { /* sessionStorage unavailable */ }
      if (this._colIndexPromise) return this._colIndexPromise;

      this._colIndexPromise = (async () => {
        const all = [];
        let page = 1;
        let pages = 1;
        do {
          try {
            const html = await fetch(`${root}/?section_id=collections-index&page=${page}`).then((r) => r.text());
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const el = doc.querySelector('[data-collections-index]');
            if (!el) break;
            pages = parseInt(el.getAttribute('data-pages') || '1', 10) || 1;
            const arr = JSON.parse(el.textContent || '[]');
            arr.forEach((c) => {
              if (c && c.t && c.u) all.push({ t: c.t, u: c.u.split('?')[0], n: this._normText(c.t) });
            });
          } catch (e) { break; }
          page += 1;
        } while (page <= pages && page <= 8); // safety cap: 8 pages (~2000 collections)

        this._colIndex = all;
        try { sessionStorage.setItem(cacheKey, JSON.stringify(all)); } catch (e) { /* quota */ }
        return all;
      })();
      return this._colIndexPromise;
    }

    /**
     * Returns collections whose normalized title contains the normalized query.
     * @param {string} term
     * @returns {Array<{title:string,url:string}>}
     */
    fuzzyCollectionMatches(term) {
      const idx = this._colIndex;
      if (!idx || !idx.length) return [];
      const q = this._normText(term);
      if (q.length < 4) return []; // too short -> too many false positives
      const out = [];
      for (const c of idx) {
        if (c.n.indexOf(q) !== -1) {
          out.push({ title: c.t, url: c.u });
          if (out.length >= 12) break;
        }
      }
      return out;
    }

    /**
     * Builds the overlay sidebar: a collections list (top) + the real Shopify facets.
     * Shown when EITHER collections or facet groups exist. No CLS when both are empty.
     * @param {DocumentFragment} fragment - tmpl.content of the fetched /search section
     * @param {Array<{title:string,url:string}>} collections - matching collections
     */
    buildFacetSidebar(fragment, collections) {
      const sidebarContainer = this.overlayContainer &&
        this.overlayContainer.querySelector('[data-predictive-search-sidebar-container]');
      if (!sidebarContainer) return;
      collections = collections || [];

      const facets = fragment && fragment.getElementById && fragment.getElementById('facets');
      const hasGroups = !!(facets && facets.querySelector('.facets__filters .filter, .facets__filters input[name^="filter."]'));
      const footerBtn = this.overlayContainer.querySelector('[data-footer-filters]');
      const mobileFilterBtn = this.overlayContainer.querySelector('[data-mobile-filter-toggle]');

      // The client-side stock filter (Lagerstatus: own vs remote stock) is ALWAYS rendered in the
      // controls row and is always useful on this store (it drives delivery time), so the rail is
      // worth showing whenever there are product results — even when Shopify returns no facet
      // groups and no collections match. Only truly hide it when there are no results at all.
      if (!hasGroups && collections.length === 0) {
        const hasResults = !!(fragment && fragment.querySelector &&
          fragment.querySelector('.card__media, .product-card, [data-product-card]'));
        if (!hasResults) {
          sidebarContainer.innerHTML = '';
          sidebarContainer.hidden = true;
          sidebarContainer.style.setProperty('display', 'none', 'important');
          if (footerBtn) { footerBtn.setAttribute('hidden', 'true'); footerBtn.style.display = 'none'; }
          if (mobileFilterBtn) mobileFilterBtn.setAttribute('hidden', 'true');
          this.overlayContainer.classList.remove('pso-filters-open');
          return;
        }
      }

      let html = '';

      // Collections list (rendered as a chip row in the top bar via CSS).
      if (collections.length) {
        const title = sidebarContainer.dataset.collectionsTitle || 'Collections';
        html += '<div class="pso-sidebar-collections">';
        html += '<h3 class="pso-sidebar__title">' + this.escapeHtml(title) + '</h3>';
        html += '<ul class="pso-sidebar-collections__list">';
        collections.forEach((c) => {
          html += '<li class="pso-sidebar-collections__item"><a class="pso-sidebar-collections__link" href="' +
            this.escapeHtml(c.url) + '">' + this.escapeHtml(c.title) + '</a></li>';
        });
        html += '</ul></div>';
      }

      // Controls row: stock dropdown + facet dropdowns as FLAT siblings (Lagerstatus, Pris, Brand)
      // so they lay out as equal columns. Rebuilt wholesale on apply (collections live outside).
      html += '<div class="pso-controls">';
      html += this._controlsInnerHTML(hasGroups ? facets : null);

      html += '</div>'; // .pso-controls

      sidebarContainer.innerHTML = html;
      sidebarContainer.hidden = false;
      // Show it: the CSS reveals the rail via `[style*="display: block"]` (it's display:none by
      // default). Set it WITHOUT !important so the desktop `display:flex` stylesheet rule can still
      // override it — an inline `!important` would beat the desktop flex layout.
      sidebarContainer.style.display = 'block';
      sidebarContainer.classList.remove('is-loading');

      if (footerBtn) { footerBtn.removeAttribute('hidden'); footerBtn.style.display = 'flex'; }
      if (mobileFilterBtn) mobileFilterBtn.removeAttribute('hidden');

      this.bindOverlayFacetListeners(sidebarContainer);
      this.applyStockFilter();
      this._markActiveFilters(sidebarContainer);
      this._facetsLoadedKey = this.getQuery().toLowerCase().trim();
    }

    /**
     * Builds the stock-filter dropdown markup (client-side filter). Checkbox state reflects
     * the current this._stockFilter Set so it survives re-renders.
     */
    _stockDropdownHTML(sidebarContainer) {
      const title = sidebarContainer.dataset.stockTitle || 'Availability';
      const ownLabel = sidebarContainer.dataset.stockOwnLabel || 'In stock';
      const remoteLabel = sidebarContainer.dataset.stockRemoteLabel || 'Backordered';
      const has = (v) => (this._stockFilter && this._stockFilter.has && this._stockFilter.has(v)) ? ' checked' : '';
      let h = '<details class="filter disclosure pso-stock-filter" name="pso-facet-group">';
      h += '<summary class="filter__toggle"><div class="flex justify-between items-center" style="width:100%">';
      h += '<span class="flex-auto font-bold">' + this.escapeHtml(title) + '</span>';
      h += '<svg class="pso-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      h += '</div></summary>';
      // Use the theme's checkbox structure (input.checkbox + label.filter__label) so the boxes get
      // the same custom 22px box + tick styling as the Brand facet checkboxes.
      h += '<div class="disclosure__panel"><ul>';
      h += '<li><input type="checkbox" class="checkbox pso-stock-cb" id="pso-stock-own" value="own"' + has('own') + '><label class="filter__label" for="pso-stock-own"><span class="flex-auto">' + this.escapeHtml(ownLabel) + '</span></label></li>';
      h += '<li><input type="checkbox" class="checkbox pso-stock-cb" id="pso-stock-remote" value="remote"' + has('remote') + '><label class="filter__label" for="pso-stock-remote"><span class="flex-auto">' + this.escapeHtml(remoteLabel) + '</span></label></li>';
      h += '</ul></div></details>';
      return h;
    }

    /**
     * Builds the inner controls HTML: stock dropdown + the real Shopify facet filters (Pris,
     * Brand) extracted as FLAT .filter siblings (no wrapper nesting) so they lay out as equal
     * flex columns alongside the stock dropdown.
     * @param {DocumentFragment|Element|null} facets - the parsed #facets element (or null)
     */
    _controlsInnerHTML(facets) {
      const sidebarContainer = this.overlayContainer &&
        this.overlayContainer.querySelector('[data-predictive-search-sidebar-container]');
      let html = this._stockDropdownHTML(sidebarContainer);
      if (facets) {
        const wrapper = document.createElement('div');
        wrapper.appendChild(facets.cloneNode(true));
        this.neutralizeFacetMarkup(wrapper);
        this._pruneFacetGroups(wrapper);
        wrapper.querySelectorAll('.facets__filters .filter').forEach((f) => { html += f.outerHTML; });
      }
      return html;
    }

    /**
     * Renders the stock dropdown immediately + skeleton placeholders where the facets will land,
     * so the filter row appears instantly instead of popping in after the fetch.
     */
    _showFacetSkeleton() {
      const c = this.overlayContainer &&
        this.overlayContainer.querySelector('[data-predictive-search-sidebar-container]');
      if (!c) return;
      // Don't clobber real facets already rendered for the current query.
      if (this._facetsLoadedKey && this._facetsLoadedKey === this.getQuery().toLowerCase().trim()) return;

      let html = '<div class="pso-controls">';
      html += this._stockDropdownHTML(c);
      html += '<span class="pso-skeleton-btn"></span><span class="pso-skeleton-btn"></span>';
      html += '</div>';

      c.innerHTML = html;
      c.hidden = false;
      c.style.display = 'block'; // reveals via CSS `[style*="display: block"]`; no !important so desktop flex can override
      this.bindOverlayFacetListeners(c);
      this.applyStockFilter();
    }

    /**
     * Marks facet buttons (summary) whose group has an active selection, so CSS can show a dot.
     * Replaces the "N filters applied" active-filters bar.
     */
    _markActiveFilters(sidebarContainer) {
      if (!sidebarContainer) return;
      sidebarContainer.querySelectorAll('.filter').forEach((f) => {
        const checked = f.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked');
        const priceSet = [...f.querySelectorAll('input[name*="price"]')].some((i) => i.value !== '' && i.value != null);
        const summary = f.querySelector('summary');
        if (summary) summary.classList.toggle('pso-filter-active', !!(checked || priceSet));
      });
    }

    /**
     * Client-side stock filter: shows/hides rendered product cards by their
     * data-stock-type (own | remote), read from .product-inventory__status.
     * State is a Set on this._stockFilter (empty/absent = show all; can hold both).
     */
    applyStockFilter() {
      if (!this.results) return;
      const active = this._stockFilter;
      const hasFilter = active && active.size > 0;
      const items = this.results.querySelectorAll('li.predictive-search__item:not(.js-submit)');
      items.forEach((li) => {
        if (!hasFilter) { li.style.removeProperty('display'); return; }
        const marker = li.querySelector('.product-inventory__status[data-stock-type]');
        const type = marker ? marker.getAttribute('data-stock-type') : null;
        if (type && active.has(type)) li.style.removeProperty('display');
        else li.style.setProperty('display', 'none', 'important');
      });
    }

    /** Count product cards currently visible (not hidden by the stock filter). */
    _visibleCardCount() {
      if (!this.results) return 0;
      let n = 0;
      this.results.querySelectorAll('li.predictive-search__item:not(.js-submit)').forEach((li) => {
        if (li.style.display !== 'none') n++;
      });
      return n;
    }

    /**
     * The stock filter (own vs remote) is client-side — it can only hide LOADED cards, so on its own
     * it never reaches products on not-yet-loaded pages. When it's active and few cards match, pull
     * more pages (each gets the filter applied on insert) until enough match or all pages are in, so
     * the filter effectively covers the whole result set.
     */
    async _fillStockFilter() {
      if (!this.overlayMode) return;
      if (!this._stockFilter || this._stockFilter.size === 0) return;
      if (this._stockFillRunning) return; // don't stack loops when toggling both boxes fast
      this._stockFillRunning = true;
      try {
        const TARGET = 16; // enough to fill the viewport + let normal scroll take over
        let guard = 0;
        while (guard++ < 20 && !this._overlayNoMore && this._stockFilter && this._stockFilter.size > 0) {
          if (this._visibleCardCount() >= TARGET) break;
          await this.loadMoreOverlayResults(); // applies the stock filter to the new cards on insert
        }
      } finally {
        this._stockFillRunning = false;
      }
    }

    /**
     * One delegated listener set on the stable sidebar container (survives innerHTML swaps).
     */
    bindOverlayFacetListeners(sidebarContainer) {
      if (this._facetListenersBound) return;
      this._facetListenersBound = true;

      sidebarContainer.addEventListener('change', (evt) => {
        const t = evt.target;
        // Stock filter checkboxes (client-side): toggle own/remote in the Set.
        if (t && t.classList && t.classList.contains('pso-stock-cb')) {
          if (!this._stockFilter) this._stockFilter = new Set();
          if (t.checked) this._stockFilter.add(t.value); else this._stockFilter.delete(t.value);
          this.applyStockFilter();
          this._markActiveFilters(sidebarContainer);
          // Client-side filter only sees loaded cards → auto-pull more pages so it covers the full
          // result set (see _fillStockFilter). No-op when the filter was just cleared.
          this._fillStockFilter();
          return;
        }
        if (t && t.name && t.name.indexOf('filter.') === 0) {
          this.applyOverlayFacets(this.collectFacetParams());
        }
      });

      // Enter in a price input applies immediately (and never submits a form / navigates away).
      sidebarContainer.addEventListener('keydown', (evt) => {
        if (evt.key !== 'Enter') return;
        const t = evt.target;
        if (t && t.name && t.name.indexOf('filter.') === 0) {
          evt.preventDefault();
          this.applyOverlayFacets(this.collectFacetParams());
        }
      });

      sidebarContainer.addEventListener('click', (evt) => {
        const showMore = evt.target.closest && evt.target.closest('.js-show-more');
        if (showMore) {
          evt.preventDefault();
          const group = showMore.closest('.filter, .facets__filters, .pso-facets') || sidebarContainer;
          group.querySelectorAll('.js-hidden').forEach((el) => {
            el.classList.remove('js-hidden');
            el.removeAttribute('hidden');
          });
          showMore.setAttribute('hidden', '');
          return;
        }
        const remover = evt.target.closest &&
          evt.target.closest('.js-clear-filter, .js-clear-all, .facets__active-filters a, .active-filters a');
        if (remover && remover.tagName === 'A' && remover.getAttribute('href')) {
          evt.preventDefault();
          let sp;
          try { sp = new URL(remover.href, window.location.origin).searchParams; }
          catch (e) { sp = new URLSearchParams(); }
          sp.set('q', this.getQuery());
          sp.set('type', 'product');
          sp.set('options[prefix]', 'last');
          this.applyOverlayFacets(sp);
        }
      });
    }

    /**
     * Collects active facet params from the overlay sidebar into URLSearchParams.
     */
    collectFacetParams() {
      const sidebarContainer = this.overlayContainer &&
        this.overlayContainer.querySelector('[data-predictive-search-sidebar-container]');
      const params = new URLSearchParams();
      params.set('q', this.getQuery());
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      if (!sidebarContainer) return params;

      sidebarContainer.querySelectorAll('input[name^="filter."]').forEach((input) => {
        const name = input.name;
        if (input.type === 'checkbox' || input.type === 'radio') {
          if (input.checked && input.value) params.append(name, input.value);
        } else if (input.value !== '' && input.value != null) {
          params.append(name, input.value);
        }
      });
      return params;
    }

    /**
     * Applies facets IN-overlay: re-fetch section with filter params, swap facets + results.
     * Debounced + abortable. No history/URL change (overlay is ephemeral).
     */
    applyOverlayFacets(searchParams) {
      clearTimeout(this._facetApplyTimer);
      this._facetApplyTimer = setTimeout(async () => {
        const sidebarContainer = this.overlayContainer &&
          this.overlayContainer.querySelector('[data-predictive-search-sidebar-container]');
        if (!sidebarContainer) return;

        if (this._facetApplyAbort) { try { this._facetApplyAbort.abort(); } catch (e) {} }
        this._facetApplyAbort = new AbortController();

        sidebarContainer.classList.add('is-loading');
        if (this.results) this.results.classList.add('is-loading');

        try {
          const url = `${this.getLocalizedSearchUrl()}?${searchParams.toString()}&section_id=main-search`;
          const res = await fetch(url, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this._facetApplyAbort.signal
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const text = await res.text();

          const tmpl = document.createElement('template');
          tmpl.innerHTML = text;

          // Preserve which facet <details> groups are open across the swap.
          const openIds = new Set();
          sidebarContainer.querySelectorAll('details[open][id]').forEach((d) => openIds.add(d.id));

          const facets = tmpl.content.getElementById('facets');
          // Rebuild the whole controls row (stock dropdown + fresh facet filters) as flat siblings;
          // the collections list lives outside .pso-controls and is untouched.
          const controls = sidebarContainer.querySelector('.pso-controls');
          if (facets && controls) {
            controls.innerHTML = this._controlsInnerHTML(facets);
            openIds.forEach((id) => {
              const sel = '#' + ((window.CSS && CSS.escape) ? CSS.escape(id) : id);
              const d = controls.querySelector(sel);
              if (d && d.tagName === 'DETAILS') d.setAttribute('open', '');
            });
            this._markActiveFilters(sidebarContainer);
          }

          // Re-render results grid: clear current product items, clone fresh cards from fragment.
          if (this.results) {
            const list = this.results.querySelector('.predictive-search__list') || this.results.querySelector('ul');
            const grid = tmpl.content.querySelector('#filter-results, .main-products-grid__results, .products-grid');
            if (list && grid) {
              list.querySelectorAll('li.predictive-search__item:not(.js-submit)').forEach((li) => li.remove());
              const viewAll = list.querySelector('li.js-submit');
              const seen = new Set();
              grid.querySelectorAll('.card, .product-card, li.js-pagination-result').forEach((card) => {
                const link = card.querySelector && card.querySelector('a[href*="/products/"]');
                if (!link) return;
                const h = (link.getAttribute('href') || '').split('?')[0];
                if (!h || seen.has(h)) return;
                seen.add(h);
                const li = document.createElement('li');
                li.className = 'predictive-search__item';
                li.setAttribute('role', 'option');
                const wrap = document.createElement('div');
                wrap.className = 'predictive-search-overlay__product-card';
                let cardEl;
                if (card.classList && card.classList.contains('card')) {
                  cardEl = card.cloneNode(true);
                } else {
                  const inner = card.querySelector('.card, .product-card');
                  cardEl = inner ? inner.cloneNode(true) : card.cloneNode(true);
                }
                wrap.appendChild(cardEl);
                li.appendChild(wrap);
                if (viewAll) list.insertBefore(li, viewAll); else list.appendChild(li);
              });
            }
          }

          // Re-apply the client-side stock filter to the swapped-in cards.
          this.applyStockFilter();

          // Load-more must now continue from THIS filtered result set (page 1). Remember the active
          // facet params so each next page is fetched with the same filters — otherwise scrolling
          // would append unfiltered products ("filter doesn't work for the next products").
          this._overlayFilterParams = searchParams;
          this._overlayPageQuery = this.getQuery();
          this._overlayLoadedPage = 1;
          this._overlayNoMore = false;
        } catch (e) {
          if (e && e.name !== 'AbortError' && typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('🔍 Predictive search: facet apply failed:', e);
          }
        } finally {
          sidebarContainer.classList.remove('is-loading');
          if (this.results) this.results.classList.remove('is-loading');
          if (typeof this.adjustTitleHeights === 'function') this.adjustTitleHeights();
        }
      }, 450);
    }

    /**
     * Escapes HTML to prevent XSS
     */
    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    disconnectedCallback() {
      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      if (this.resetBtn) {
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      }
      if (this.overlay) {
      this.overlay.removeEventListener('click', this.overlayClickHandler);
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
